<template>
  <div class="oform-page py-3">
    <header class="oform-toolbar">
      <div class="oform-toolbar__title">
        <span class="oform-toolbar__accent" aria-hidden="true"></span>
        <h1>{{ pageTitle }}</h1>
      </div>
      <div class="oform-toolbar__meta">
        <label class="marketplace-toggle" :class="{ 'is-active': marketplaceMode === 'amazon' }" title="Bill an Amazon marketplace order">
          <input type="checkbox" :checked="marketplaceMode === 'amazon'" @change="toggleMarketplaceMode('amazon', $event.target.checked)">
          <span>Bill Amazon Order</span>
        </label>
        <label class="marketplace-toggle" :class="{ 'is-active': marketplaceMode === 'flipkart' }" title="Bill a Flipkart marketplace order">
          <input type="checkbox" :checked="marketplaceMode === 'flipkart'" @change="toggleMarketplaceMode('flipkart', $event.target.checked)">
          <span>Bill Flipkart Order</span>
        </label>
        <CButton v-if="editOrderId" color="secondary" size="sm" variant="outline" @click="$router.push({ path: '/orders' })">
          Back to Orders
        </CButton>
        <CButton color="secondary" size="sm" variant="outline" :disabled="isLoading" @click="reloadCurrent">
          {{ isLoading ? 'Loading…' : 'Refresh' }}
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="oform-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="oform-alert">{{ successMessage }}</CAlert>

    <CRow class="oform-grid">
      <CCol lg="8" class="oform-main">
        <section class="oform-section">
          <header class="oform-section__head">
            <h2><span class="step-num">1</span> {{ isMarketplaceOrder ? marketplaceLabel + ' Order Details' : 'Find Customer' }}</h2>
            <span class="oform-section__hint">{{ isMarketplaceOrder ? 'Phone is billed under 0000000001' : 'Search by phone to auto-fill' }}</span>
          </header>
          <div class="oform-section__body">
            <CForm v-if="!isMarketplaceOrder" @submit.prevent="searchContact">
              <label class="oform-label">Phone Number<span class="oform-label__required" aria-hidden="true">*</span></label>
              <div class="phone-row">
                <AppDropdown
                  class="phone-row__country"
                  :value="countryCode"
                  :options="countryOptions"
                  searchable
                  search-placeholder="Search country…"
                  @update:value="countryCode = $event"
                />
                <AppInput class="phone-row__input" no-margin type="tel" placeholder="Phone number" :value="form.contact_phone" :maxlength="15" @update:value="setPhone($event)" />
                <CButton type="submit" color="primary" class="phone-row__btn" :disabled="isSearching">
                  {{ isSearching ? 'Searching…' : 'Search' }}
                </CButton>
              </div>
              <small :class="['phone-row__hint', searchHintClass]">{{ searchHint || 'Search existing customer using phone number' }}</small>
            </CForm>

            <div v-else class="marketplace-fields">
              <CRow>
                <CCol md="6">
                  <AppInput
                    :label="marketplaceLabel + ' Order ID'"
                    required
                    :placeholder="marketplaceOrderIdPlaceholder"
                    :value="form.marketplace_order_id"
                    @update:value="form.marketplace_order_id = String($event || '').trim()"
                  />
                </CCol>
                <CCol md="6">
                  <AppInput
                    :label="marketplaceLabel + ' Invoice Number'"
                    required
                    :placeholder="'Invoice number from ' + marketplaceLabel"
                    :value="form.invoice_number"
                    @update:value="form.invoice_number = String($event || '').trim()"
                  />
                </CCol>
              </CRow>
              <CRow>
                <CCol md="6">
                  <AppInput
                    :label="marketplaceLabel + ' Tracking ID'"
                    placeholder="Tracking / AWB number"
                    :value="form.tracking_id"
                    @update:value="form.tracking_id = String($event || '').trim()"
                  />
                </CCol>
                <CCol md="6" class="form-group">
                  <label class="oform-label">Invoice Date<span class="oform-label__required" aria-hidden="true">*</span></label>
                  <SingleDatePicker :value="form.marketplace_invoice_date" @input="form.marketplace_invoice_date = $event" />
                </CCol>
              </CRow>
              <div class="marketplace-phone-note">
                Phone number is disabled for {{ marketplaceLabel }} orders and saved as <strong>0000000001</strong>.
              </div>
            </div>
          </div>
        </section>

        <section v-if="hasContactHistory && !isMarketplaceOrder" class="oform-section history-section">
          <header
            class="oform-section__head history-section__head"
            role="button"
            tabindex="0"
            :aria-expanded="!historyCollapsed"
            @click="historyCollapsed = !historyCollapsed"
            @keydown.enter.prevent="historyCollapsed = !historyCollapsed"
            @keydown.space.prevent="historyCollapsed = !historyCollapsed"
          >
            <h2>Contact History</h2>
            <div class="history-section__head-meta">
              <span class="oform-section__hint">{{ historyCollapsed ? historyCondensedSummary : 'Past activity for this number' }}</span>
              <span :class="['history-section__chevron', { 'is-open': !historyCollapsed }]" aria-hidden="true">▾</span>
            </div>
          </header>
          <div v-show="!historyCollapsed" class="oform-section__body">
            <div class="history-stats">
              <div class="history-stat">
                <span class="history-stat__label">Calls</span>
                <span class="history-stat__value">{{ contactCalls.length }}</span>
              </div>
              <div class="history-stat">
                <span class="history-stat__label">Orders</span>
                <span class="history-stat__value">{{ contactOrders.length }}</span>
              </div>
              <div class="history-stat">
                <span class="history-stat__label">Lifetime</span>
                <span class="history-stat__value">{{ formatCurrency(lifetimeSpend) }}</span>
              </div>
              <div class="history-stat">
                <span class="history-stat__label">Last Call</span>
                <span class="history-stat__value history-stat__value--small">{{ lastCallSummary }}</span>
              </div>
            </div>

            <div class="history-grid">
              <div class="history-col">
                <h3 class="history-title">Recent Calls</h3>
                <div v-if="contactCalls.length" class="history-list">
                  <div v-for="(call, idx) in contactCalls" :key="`call-${idx}`" class="history-item">
                    <div class="history-item__line">
                      <span class="history-item__date">{{ formatCallDate(call.date) }}</span>
                      <span :class="['history-pill', callStatusClass(call.status)]">{{ call.status }}</span>
                    </div>
                    <div class="history-item__sub">{{ call.caller || 'Unknown caller' }}</div>
                  </div>
                </div>
                <div v-else class="history-empty">No previous calls.</div>
              </div>

              <div class="history-col">
                <h3 class="history-title">Recent Orders</h3>
                <div v-if="contactOrders.length" class="history-list">
                  <div v-for="order in contactOrders" :key="order.id" class="history-item">
                    <div class="history-item__line">
                      <span class="history-item__strong">{{ order.order_number }}</span>
                      <span class="history-item__amount">{{ formatCurrency(order.grand_total) }}</span>
                    </div>
                    <div class="history-item__sub">
                      {{ formatOrderDate(order.order_date) }}
                      <span class="history-dot">·</span>
                      <span :class="paymentStatusClass(order.payment_status)">{{ formatLabel(order.payment_status) }}</span>
                      <span class="history-dot">·</span>
                      {{ formatLabel(order.order_status) }}
                    </div>
                    <ul v-if="order.order_items && order.order_items.length" class="history-products">
                      <li v-for="item in order.order_items" :key="item.id">
                        <span class="history-products__name">{{ item.name }}</span>
                        <span class="history-products__qty">× {{ item.quantity }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div v-else class="history-empty">No previous orders.</div>
              </div>
            </div>
          </div>
        </section>

        <section class="oform-section">
          <header class="oform-section__head">
            <h2><span class="step-num">2</span> Customer Information</h2>
            <label class="head-toggle">
              <input type="checkbox" :checked="includeGstin" @change="toggleGstin($event.target.checked)">
              <span>Include GST IN</span>
            </label>
          </header>
          <div class="oform-section__body">
            <CRow>
              <CCol md="8">
                <AppInput
                  label="Full Name"
                  required
                  placeholder="Enter Full Name"
                  :value="form.contact_name"
                  @update:value="form.contact_name = String($event || '').replace(/[^a-zA-Z\s]/g, '')"
                />
              </CCol>
              <CCol md="4">
                <AppInput
                  label="Alternate Phone"
                  required
                  type="tel"
                  :maxlength="15"
                  :disabled="isMarketplaceOrder"
                  placeholder="Backup number"
                  :value="form.contact_phone_2"
                  @update:value="setAlternatePhone($event)"
                />
              </CCol>
            </CRow>

            <CRow v-if="includeGstin">
              <CCol md="12">
                <AppInput
                  label="GST IN"
                  placeholder="Enter GST IN"
                  :value="form.contact_gstn"
                  @update:value="form.contact_gstn = String($event || '').trim()"
                />
              </CCol>
            </CRow>

            <CRow>
              <CCol md="4" :class="{ 'is-attention-pulse': pincodeAttentionPulse }">
                <AppInput
                  ref="contactPincodeInput"
                  label="Pincode"
                  placeholder="6-digit pincode"
                  :maxlength="6"
                  :value="form.contact_pincode"
                  @update:value="onPincodeInput($event)"
                />
              </CCol>
              <CCol md="8" class="form-group">
                <label class="oform-label">
                  Post Office
                  <span v-if="isPincodeLoading" class="oform-label__status">Looking up…</span>
                  <span v-else-if="form.contact_pincode.length === 6 && !postOfficeOptions.length" class="oform-label__status oform-label__status--warn">No matches</span>
                </label>
                <AppDropdown
                  :value="form.contact_postofficename"
                  :options="postOfficeDropdownOptions"
                  :placeholder="postOfficeOptions.length ? 'Select a post office' : 'Enter pincode to auto-fill'"
                  @update:value="onPostOfficeSelect($event)"
                />
              </CCol>
            </CRow>

            <CRow>
              <CCol md="4">
                <AppInput label="District" :value="form.contact_district" disabled />
              </CCol>
              <CCol md="4">
                <AppInput label="State" :value="form.contact_state" disabled />
              </CCol>
              <CCol md="4">
                <AppInput
                  label="Ward No"
                  type="number"
                  :value="form.ward_no"
                  @update:value="form.ward_no = Number($event || 0)"
                />
              </CCol>
            </CRow>

            <CRow>
              <CCol md="12" class="form-group">
                <label class="oform-label">Local Body</label>
                <AppDropdown
                  :value="selectedLocalBodyName"
                  :options="localBodyDropdownOptions"
                  :placeholder="localBodyOptions.length ? 'Search and select a local body' : 'Auto-loads after pincode'"
                  searchable
                  search-placeholder="Search local body…"
                  @update:value="onLocalBodySelect($event)"
                />
              </CCol>
            </CRow>

            <CRow>
              <CCol md="12">
                <AppInput
                  label="Address"
                  required
                  multiline
                  :rows="2"
                  placeholder="Enter Address"
                  :value="form.contact_address"
                  @update:value="form.contact_address = String($event || '')"
                />
              </CCol>
            </CRow>
          </div>
        </section>

        <section class="oform-section">
          <header class="oform-section__head">
            <h2><span class="step-num">3</span> Billing Address</h2>
            <label class="head-toggle">
              <input type="checkbox" :checked="addBillingAddress" @change="toggleBilling($event.target.checked)">
              <span>{{ addBillingAddress ? 'Enabled' : 'Disabled' }}</span>
            </label>
          </header>
          <div v-if="addBillingAddress" class="oform-section__body">
            <CRow>
              <CCol md="12">
                <AppInput
                  label="Billing Name"
                  placeholder="Billing Name"
                  :value="form.billing_name"
                  @update:value="form.billing_name = String($event || '')"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="12">
                <AppInput
                  label="Billing Address"
                  multiline
                  :rows="2"
                  placeholder="Billing Address"
                  :value="form.billing_address"
                  @update:value="form.billing_address = String($event || '')"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="6">
                <AppInput
                  label="Billing Pincode"
                  placeholder="Billing Pincode"
                  :value="form.billing_pincode"
                  @update:value="form.billing_pincode = String($event || '').replace(/\D/g, '').slice(0, 6)"
                />
              </CCol>
              <CCol md="6">
                <AppInput
                  label="Billing Post Office"
                  placeholder="Billing Post Office"
                  :value="form.billing_postofficename"
                  @update:value="form.billing_postofficename = String($event || '')"
                />
              </CCol>
            </CRow>
            <CRow>
              <CCol md="6">
                <AppInput
                  label="Billing District"
                  placeholder="Billing District"
                  :value="form.billing_district"
                  @update:value="form.billing_district = String($event || '')"
                />
              </CCol>
              <CCol md="6">
                <AppInput
                  label="Billing State"
                  placeholder="Billing State"
                  :value="form.billing_state"
                  @update:value="form.billing_state = String($event || '')"
                />
              </CCol>
            </CRow>
          </div>
          <div v-else class="oform-section__body">
            <div class="oform-empty">Toggle on to capture a separate billing address.</div>
          </div>
        </section>

        <section v-if="showCropsSection" class="oform-section">
          <header class="oform-section__head">
            <h2><span class="step-num">4</span> Customer Crops</h2>
            <span class="oform-section__hint">Optional</span>
          </header>
          <div class="oform-section__body">
            <div v-if="allCrops.length" class="crop-table">
              <table class="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Crop</th>
                    <th>Area</th>
                    <th>Unit</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(crop, index) in allCrops" :key="`crop-${index}`">
                    <td>{{ crop.crop }}</td>
                    <td>{{ crop.area }}</td>
                    <td>{{ formatCropUnit(crop.unit) }}</td>
                    <td>{{ crop.count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="oform-empty">No crops added yet.</div>

            <div class="crop-add">
              <div class="crop-add__field">
                <label class="oform-label">Crop</label>
                <AppDropdown
                  :value="cropDraft.crop"
                  :options="cropOptions"
                  placeholder="Select Crop"
                  searchable
                  search-placeholder="Search crop…"
                  @update:value="cropDraft.crop = $event"
                />
              </div>
              <div class="crop-add__field">
                <label class="oform-label">Area</label>
                <AppInput
                  no-margin
                  type="number"
                  :value="cropDraft.area"
                  @update:value="cropDraft.area = Number($event || 0)"
                />
              </div>
              <div class="crop-add__field">
                <label class="oform-label">Unit</label>
                <AppDropdown
                  :value="cropDraft.unit"
                  :options="cropUnitOptions"
                  @update:value="cropDraft.unit = $event"
                />
              </div>
              <div class="crop-add__field">
                <label class="oform-label">Count</label>
                <AppInput
                  no-margin
                  type="number"
                  :value="cropDraft.count"
                  @update:value="cropDraft.count = Number($event || 0)"
                />
              </div>
              <div class="crop-add__action">
                <CButton color="primary" class="w-100" :disabled="!cropDraft.crop" @click="addCropInfo">+ Add</CButton>
              </div>
            </div>
          </div>
        </section>

        <section class="oform-section">
          <header class="oform-section__head">
            <h2><span class="step-num">5</span> Order Items</h2>
          </header>
          <div class="oform-section__body">
            <CRow>
              <CCol v-if="!isMarketplaceOrder" md="4" class="form-group">
                <label class="oform-label">Order Type<span class="oform-label__required" aria-hidden="true">*</span></label>
                <AppDropdown
                  :value="form.order_type"
                  :options="orderTypeOptions"
                  placeholder="Select Order Type"
                  @update:value="onOrderTypeSelect($event)"
                />
              </CCol>
              <CCol :md="isMarketplaceOrder ? 12 : 8" class="form-group">
                <label class="oform-label">Search Product</label>
                <AppDropdown
                  :value="productDraft.item_id"
                  :options="itemOptions"
                  placeholder="Select a product"
                  searchable
                  search-placeholder="Search product…"
                  @update:value="onDraftProductSelect($event)"
                />
              </CCol>
            </CRow>

            <div class="product-add">
              <div class="product-add__field">
                <label class="oform-label">Price</label>
                <AppInput no-margin :value="productDraftPrice" disabled />
              </div>
              <div class="product-add__field product-add__field--qty">
                <label class="oform-label">Qty</label>
                <AppInput
                  no-margin
                  type="number"
                  :value="productDraft.quantity"
                  @update:value="productDraft.quantity = Math.max(1, Number($event || 1))"
                />
              </div>
              <div class="product-add__field">
                <label class="oform-label">Total</label>
                <AppInput no-margin :value="productDraftTotal" disabled />
              </div>
              <div class="product-add__action">
                <CButton
                  color="primary"
                  class="w-100"
                  :disabled="!productDraft.item_id || Number(productDraft.quantity || 0) <= 0"
                  @click="addSelectedProduct"
                >+ Add</CButton>
              </div>
            </div>

            <div v-if="!form.order_items.length" class="oform-empty mt-3">
              No items added yet. Pick a product above to add it to the order.
            </div>

            <ul v-else class="item-list">
              <li v-for="(item, index) in form.order_items" :key="`order-item-${index}`" class="item-list__row">
                <div class="item-list__main">
                  <div class="item-list__name">{{ item.name }}</div>
                  <div class="item-list__meta">{{ formatCurrency(item.price) }} each</div>
                </div>
                <div class="item-list__qty">
                  <label class="oform-label-inline">Qty</label>
                  <AppInput
                    no-margin
                    type="number"
                    :value="item.quantity"
                    @update:value="updateItemQuantity(index, $event)"
                  />
                </div>
                <div class="item-list__total">
                  <span class="oform-label-inline">Total</span>
                  <strong>{{ formatCurrency(item.total) }}</strong>
                </div>
                <CButton color="danger" variant="outline" size="sm" class="item-list__remove" @click="removeItem(index)">Remove</CButton>
              </li>
            </ul>

            <CRow v-if="form.order_items.length && eligibleDiscountTier" class="mt-3">
              <CCol md="12">
                <div class="preset-banner">
                  <div class="preset-banner__info">
                    <span class="preset-banner__dot" aria-hidden="true"></span>
                    <div>
                      <div class="preset-banner__title">
                        Eligible: {{ eligibleDiscountTierLabel }}
                        <button
                          type="button"
                          class="preset-banner__info-btn"
                          aria-label="Show discount chart"
                          title="Discount chart"
                          @click="showDiscountChart = true"
                        >
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="11" x2="12" y2="17" />
                            <line x1="12" y1="7" x2="12.01" y2="7" />
                          </svg>
                        </button>
                      </div>
                      <div class="preset-banner__sub">Saves {{ formatCurrency(eligibleDiscountAmount) }} on this order</div>
                    </div>
                  </div>
                  <label class="preset-banner__toggle" :class="{ 'is-on': applyPreset }">
                    <input
                      type="checkbox"
                      :checked="applyPreset"
                      @change="onApplyPresetToggle($event.target.checked)"
                    />
                    <span class="preset-banner__track" aria-hidden="true">
                      <span class="preset-banner__thumb"></span>
                    </span>
                    <span class="preset-banner__label">{{ applyPreset ? 'Applied' : 'Apply' }}</span>
                  </label>
                </div>
              </CCol>
            </CRow>

            <CRow v-if="form.order_items.length" class="mt-3">
              <CCol md="6" class="form-group">
                <label class="oform-label">Discount</label>
                <div class="discount-row" :class="{ 'is-locked': discountFieldsLocked }">
                  <AppDropdown
                    class="discount-row__mode"
                    :value="discountMode"
                    :options="discountModeOptions"
                    :disabled="discountFieldsLocked"
                    @update:value="onDiscountModeChange($event)"
                  />
                  <AppInput
                    no-margin
                    type="number"
                    :value="discountInput"
                    :disabled="discountFieldsLocked"
                    @update:value="onDiscountInput($event)"
                  />
                </div>
                <small class="text-muted">
                  After discount: {{ formatCurrency(form.total_after_discount) }}
                  <span v-if="applyPreset" class="preset-pill">Preset applied</span>
                  <span v-else-if="!canOverrideCharges" class="preset-pill preset-pill--muted">Locked — use the toggle above</span>
                </small>
              </CCol>
              <CCol md="6">
                <AppInput
                  label="Shipping Charge (₹)"
                  type="number"
                  :value="form.shipping_charge"
                  :disabled="shippingFieldLocked"
                  @update:value="onShippingInput($event)"
                />
                <small v-if="shippingFieldLocked && !shippingZeroReason" class="text-muted">
                  <span class="preset-pill preset-pill--muted">Locked — auto-calculated</span>
                </small>
                <div v-if="shippingZeroReason" class="oform-shipping-zero" role="alert">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <div class="oform-shipping-zero__body">
                    <strong>Shipping is ₹0 — order can't be placed.</strong>
                    <span>{{ shippingZeroMessage }}</span>
                  </div>
                  <button type="button" class="oform-shipping-zero__btn" @click="focusContactPincode">
                    {{ shippingZeroReason === 'post_office_missing' ? 'Pick post office' : 'Fix pincode' }}
                  </button>
                </div>
              </CCol>
            </CRow>
          </div>
        </section>

        <section class="oform-section">
          <header class="oform-section__head">
            <h2><span class="step-num">6</span> Payment &amp; Shipping</h2>
          </header>
          <div class="oform-section__body">
            <CRow>
              <CCol v-if="!isMarketplaceOrder" md="4" class="form-group">
                <label class="oform-label">Payment Method<span class="oform-label__required" aria-hidden="true">*</span></label>
                <AppDropdown
                  :value="form.payment_method"
                  :options="visiblePaymentMethods"
                  placeholder="Select payment method"
                  @update:value="onPaymentMethodSelect($event)"
                />
                <div v-if="codBlocked" class="oform-cod-block-note" role="status">
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>COD unavailable — this customer has a prior returned COD order. Prepaid only.</span>
                </div>
                <div v-if="form.payment_method === 'adjustment'" class="oform-adjusted-against">
                  <AppInput
                    no-margin
                    label="Adjusted Against"
                    required
                    placeholder="Referring order number"
                    :value="form.adjusted_order"
                    @update:value="form.adjusted_order = $event"
                  />
                </div>
              </CCol>
              <CCol :md="isMarketplaceOrder ? 12 : 4" class="form-group">
                <label class="oform-label">Payment Date</label>
                <SingleDatePicker :value="form.payment_date" @input="form.payment_date = $event" />
              </CCol>
              <CCol v-if="!isMarketplaceOrder" md="4" class="form-group">
                <label class="oform-label">Shipping Method<span class="oform-label__required" aria-hidden="true">*</span></label>
                <AppDropdown
                  :value="form.shipping_method"
                  :options="shippingMethodOptions"
                  placeholder="Select shipping method"
                  @update:value="onShippingMethodSelect($event)"
                />
              </CCol>
            </CRow>
            <div class="amount-due">
              <div class="amount-due__body">
                <span class="amount-due__label">Amount To Pay</span>
                <span class="amount-due__value">{{ amountToPayLabel }}</span>
              </div>
              <span class="amount-due__hint">Includes GST &amp; shipping</span>
            </div>
          </div>
        </section>
      </CCol>

      <CCol lg="4" class="oform-aside">
        <div class="oform-sticky">
          <section class="oform-section summary-section">
            <header class="oform-section__head">
              <h2>Order Summary</h2>
            </header>
            <div class="oform-section__body">
              <div class="summary-line"><span>Items</span><strong>{{ form.item_count }}</strong></div>
              <div class="summary-line"><span>Subtotal (incl. GST)</span><strong>{{ formatCurrency(form.total) }}</strong></div>
              <div class="summary-line"><span>Discount</span><strong>−{{ formatCurrency(form.discount) }}</strong></div>
              <div class="summary-line"><span>After Discount (incl. GST)</span><strong>{{ formatCurrency(form.total_after_discount) }}</strong></div>
              <div class="summary-line">
                <span class="summary-line__label">
                  Shipping
                  <button
                    v-if="shippingRateChipLabel"
                    type="button"
                    class="summary-distance-chip"
                    :title="shippingRateChipTitle"
                    aria-label="View shipping rate chart"
                    @click="showShippingTariff = true"
                  >
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {{ shippingRateChipLabel }}
                    <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </button>
                </span>
                <strong>{{ formatCurrency(form.shipping_charge) }}</strong>
              </div>
              <div v-if="form.cod_fee > 0" class="summary-line"><span>COD Fee</span><strong>{{ formatCurrency(form.cod_fee) }}</strong></div>
              <div class="summary-line summary-line--total"><span>Grand Total</span><strong>{{ formatCurrency(form.grand_total) }}</strong></div>
              <div v-if="form.gst_amt > 0" class="summary-line summary-line--note"><span>Includes {{ gstSummaryLabel }}</span><strong>{{ formatCurrency(form.gst_amt) }}</strong></div>

              <div class="summary-actions">
                <CButton color="success" class="w-100 summary-actions__primary" :disabled="isSubmitting || !isFormReady" @click="openSubmitConfirm">
                  {{ isSubmitting ? 'Saving…' : (editOrderId ? 'Update Order' : 'Save Order') }}
                </CButton>
                <button type="button" class="summary-actions__whatsapp" :disabled="!form.contact_phone" @click="copyAndShareWhatsapp">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                    <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.86 11.86 0 0 0 5.64 1.44h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.39-8.42zM12.04 21.6h-.01a9.7 9.7 0 0 1-4.95-1.36l-.36-.21-3.8 1 1.02-3.7-.23-.38a9.72 9.72 0 0 1-1.49-5.18c0-5.37 4.37-9.74 9.74-9.74 2.6 0 5.05 1.01 6.89 2.85a9.68 9.68 0 0 1 2.85 6.89c0 5.37-4.37 9.83-9.66 9.83zm5.34-7.27c-.29-.15-1.73-.86-2-.96-.27-.1-.46-.15-.66.15-.2.29-.76.96-.93 1.15-.17.2-.34.22-.63.07-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.5l-.56-.01c-.2 0-.51.07-.78.37s-1.03 1.01-1.03 2.46c0 1.45 1.05 2.86 1.2 3.05.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.08 1.73-.71 1.98-1.39.24-.68.24-1.27.17-1.39-.07-.12-.27-.2-.56-.34z" />
                  </svg>
                  <span>Copy &amp; Send via WhatsApp</span>
                </button>
                <CButton color="danger" variant="outline" class="w-100 summary-actions__reset" @click="resetForm">Reset</CButton>
              </div>
            </div>
          </section>
        </div>
      </CCol>
    </CRow>

    <AppConfirmDialog
      :visible="showSubmitConfirm"
      :title="editOrderId ? 'Save changes to this order?' : 'Place this order?'"
      :message="submitConfirmMessage"
      :confirm-label="editOrderId ? 'Save Changes' : 'Place Order'"
      busy-label="Submitting…"
      :busy="isSubmitting"
      variant="info"
      :close-on-backdrop="!isSubmitting"
      @confirm="confirmSubmit"
      @cancel="cancelSubmitConfirm"
    />

    <transition name="sale-success-fade">
      <div v-if="showSuccessModal" class="sale-success" role="dialog" aria-modal="true" aria-labelledby="sale-success-title" @mousedown.self="closeSuccessModal">
        <div class="sale-success__panel" @mousedown.stop>
          <button type="button" class="sale-success__close" aria-label="Close" @click="closeSuccessModal">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div class="sale-success__animation" aria-hidden="true">
            <span class="sale-success__ring"></span>
            <span class="sale-success__ring sale-success__ring--delay"></span>
            <svg class="sale-success__check" viewBox="0 0 64 64">
              <circle class="sale-success__check-circle" cx="32" cy="32" r="28" />
              <path class="sale-success__check-tick" d="M20 33 L29 42 L46 24" />
            </svg>
          </div>

          <h3 id="sale-success-title" class="sale-success__title">
            {{ lastSavedRequiresApproval ? 'Submitted for Approval' : 'Order Placed!' }}
          </h3>
          <p class="sale-success__subtitle">
            <template v-if="lastSaved">Order <strong>#{{ lastSaved.orderId }}</strong> · {{ lastSaved.contactName }}</template>
          </p>
          <p v-if="lastSavedRequiresApproval" class="sale-success__notice">
            This order is held for approval until a privileged user signs off from the Inbox. Invoice + WhatsApp will be available once approved.
          </p>

          <dl v-if="lastSaved" class="sale-success__meta">
            <div class="sale-success__meta-row">
              <dt>Items</dt><dd>{{ lastSaved.itemCount }}</dd>
            </div>
            <div class="sale-success__meta-row">
              <dt>Grand Total</dt><dd>{{ formatCurrency(lastSaved.grandTotal) }}</dd>
            </div>
            <div class="sale-success__meta-row">
              <dt>Phone</dt><dd>+{{ lastSaved.dialCode || '91' }} {{ lastSaved.contactPhone }}</dd>
            </div>
          </dl>

          <div class="sale-success__actions">
            <button type="button" class="sale-success__btn sale-success__btn--ghost" :disabled="!(lastSaved && lastSaved.orderId) || lastSavedRequiresApproval" @click="downloadInvoice">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Invoice
            </button>
            <button type="button" class="sale-success__btn sale-success__btn--primary" :disabled="!(lastSaved && lastSaved.contactPhone) || lastSavedRequiresApproval" @click="sendInvoiceWhatsapp">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M20.52 3.48A11.93 11.93 0 0 0 12.04 0C5.5 0 .18 5.32.18 11.86c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.86 11.86 0 0 0 5.64 1.44h.01c6.54 0 11.86-5.32 11.86-11.86 0-3.17-1.23-6.15-3.39-8.42zM12.04 21.6h-.01a9.7 9.7 0 0 1-4.95-1.36l-.36-.21-3.8 1 1.02-3.7-.23-.38a9.72 9.72 0 0 1-1.49-5.18c0-5.37 4.37-9.74 9.74-9.74 2.6 0 5.05 1.01 6.89 2.85a9.68 9.68 0 0 1 2.85 6.89c0 5.37-4.37 9.83-9.66 9.83zm5.34-7.27c-.29-.15-1.73-.86-2-.96-.27-.1-.46-.15-.66.15-.2.29-.76.96-.93 1.15-.17.2-.34.22-.63.07-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.5l-.56-.01c-.2 0-.51.07-.78.37s-1.03 1.01-1.03 2.46c0 1.45 1.05 2.86 1.2 3.05.15.2 2.08 3.18 5.04 4.46.7.3 1.25.48 1.68.62.71.22 1.35.19 1.86.12.57-.08 1.73-.71 1.98-1.39.24-.68.24-1.27.17-1.39-.07-.12-.27-.2-.56-.34z" />
              </svg>
              Send to WhatsApp
            </button>
          </div>
        </div>
      </div>
    </transition>

    <ProfessionalCourierTariffModal
      v-if="form.shipping_method === 'professional_courier'"
      :visible="showShippingTariff"
      :state-name="form.contact_state"
      @close="showShippingTariff = false"
    />
    <ShippingTariffModal
      v-else
      :visible="showShippingTariff"
      :distance-km="shippingDistanceKm"
      :zone="shippingZone"
      :zone-label="shippingZoneLabel"
      @close="showShippingTariff = false"
    />

    <DiscountChartModal
      :show.sync="showDiscountChart"
      :current-organisation-id="$store.state.activeOrganisationId"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppInput from '@/components/AppInput.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import ShippingTariffModal from '@/components/ShippingTariffModal.vue'
import ProfessionalCourierTariffModal from '@/components/ProfessionalCourierTariffModal.vue'
import DiscountChartModal from '@/components/DiscountChartModal.vue'
import { getStoredRoles } from '@/auth/access.js'
import { countryDropdownOptions, dialCodeFor, findCountryByName, findCountryByDialCode, maxPhoneLengthFor, isValidPhoneLength, DEFAULT_COUNTRY_NAME } from '@/utils/countries.js'
import { eligibleDiscountBuckets, eligibleDiscountTotal, eligibleDiscountLabel } from '@/utils/discountPresets.js'

const ORDER_TYPE_DEFAULT = ''

// Marketplace orders (Amazon / Flipkart) carry no real customer number —
// the phone is billed under this fixed placeholder so the contact/order
// rows stay valid while the actual marketplace identifiers (order id,
// invoice, tracking) live in their own fields. Both channels behave
// identically; only the labels differ. Kept in one place so the
// disable-and-force logic and the submit payload can't drift apart.
const AMAZON_ORDER_TYPE = 'amazon'
const FLIPKART_ORDER_TYPE = 'flipkart'
const MARKETPLACE_ORDER_TYPES = [AMAZON_ORDER_TYPE, FLIPKART_ORDER_TYPE]
const MARKETPLACE_PLACEHOLDER_PHONE = '0000000001'
const MARKETPLACE_LABELS = { [AMAZON_ORDER_TYPE]: 'Amazon', [FLIPKART_ORDER_TYPE]: 'Flipkart' }
const MARKETPLACE_ORDER_ID_PLACEHOLDERS = {
  [AMAZON_ORDER_TYPE]: 'e.g. 408-1234567-1234567',
  [FLIPKART_ORDER_TYPE]: 'e.g. OD123456789012345678'
}

// Mirrors DiscountPresetController::PRIVILEGED_ROLES on the server.
// Gates both the discount field and the shipping-charge field — only
// these roles may manually edit either value. Everyone else gets the
// auto-applied preset for discount and the auto-calc (COD/IP) result
// for shipping. Kept in sync with TeleSales.vue.
const CHARGE_OVERRIDE_ROLES = ['superadmin', 'admin', 'accountant', 'finance_manager', 'senior_accountant_2']

export default {
  name: 'OrderForm',
  components: { AppDropdown, AppInput, SingleDatePicker, AppConfirmDialog, ShippingTariffModal, ProfessionalCourierTariffModal, DiscountChartModal },
  data () {
    return {
      isLoading: false,
      isSearching: false,
      isPincodeLoading: false,
      isSubmitting: false,
      // Flips on for ~2.5s when we scroll-to-pincode after a ₹0-shipping
      // block, to draw the eye to the field that needs attention.
      pincodeAttentionPulse: false,
      // Submit confirmation + post-create celebration. Mirrors TeleSales
      // so the save flow feels the same on both forms: confirm dialog
      // gates every submit; the animated success modal (with invoice +
      // WhatsApp actions) only fires on new-order creation, not on edit.
      showSubmitConfirm: false,
      showSuccessModal: false,
      lastSaved: null,
      lastSavedRequiresApproval: false,
      // Edit mode is driven by ?order_id=X in the URL. When set, the form
      // hydrates from the existing order and submitOrder() PUTs instead of
      // POSTs. NO_CACHE_VIEWS in TheContainer keeps OrderForm out of the
      // keep-alive cache, so each Edit-from-Orders click mounts fresh.
      editOrderId: null,
      editOrderNumber: '',
      isLoadingExisting: false,
      errorMessage: '',
      successMessage: '',
      searchHint: '',
      countryCode: DEFAULT_COUNTRY_NAME,
      countryOptions: countryDropdownOptions(),
      includeGstin: false,
      // GSTIN found on the searched contact's history (address/last order).
      // Used to prefill the GST IN field ONLY when staff tick "Include GST IN"
      // — never to auto-enable the toggle, which silently made orders B2B.
      contactGstinOnFile: '',
      addBillingAddress: false,
      // Marketplace billing mode — toggled from the toolbar. Holds the active
      // marketplace order_type ('amazon' | 'flipkart') or '' when off.
      // Disables the phone search, forces the contact phone to
      // MARKETPLACE_PLACEHOLDER_PHONE, and swaps the customer-lookup block
      // for the marketplace identifier fields.
      marketplaceMode: '',
      discountMode: 'amount',
      discountInput: 0,
      // Discount presets: tier list for the form's org, the toggle state
      // (off by default — the user must opt in), and the org we last
      // fetched for so we don't refetch on unrelated re-renders.
      discountPresets: [],
      applyPreset: false,
      formOrganisationId: null,
      selectedLocalBodyName: '',
      postOfficeRecords: [],
      postOfficeOptions: [],
      localBodyOptions: [],
      existingCrops: [],
      contactCrops: [],
      contactCalls: [],
      contactOrders: [],
      // Set true when /api/contacts/phone returns cod_blocked=1, i.e.
      // this customer has a prior returned (or partially-returned)
      // COD order. Used to hide COD from the payment dropdown and
      // surface a banner. Server still validates on submit.
      codBlocked: false,
      historyCollapsed: false,
      amountToPay: 0,
      // Why the auto-calculated shipping charge is what it is, surfaced as a
      // chip under the shipping charge field so staff can explain it. All
      // three come from /api/cal_cod_charges. Which are populated depends on
      // the India Post tariff in force: the zone chart fills zone/zoneLabel
      // and leaves distance null; the legacy km chart fills all three.
      shippingDistanceKm: null,
      shippingZone: null,
      shippingZoneLabel: '',
      showShippingTariff: false,
      showDiscountChart: false,
      cropDraft: {
        crop: '',
        area: 0,
        unit: 'cent',
        count: 0
      },
      productDraft: {
        item_id: '',
        quantity: 1
      },
      gstStates: [],
      orderTypeOptions: [],
      paymentMethodOptions: [],
      shippingMethodOptions: [],
      itemOptions: [],
      cropOptions: [],
      cropUnitOptions: [
        { value: 'cent', label: 'Cent' },
        { value: 'acre', label: 'Acre' },
        { value: 'hectare', label: 'Hectare' },
        { value: 'metersquare', label: 'Square Meter' }
      ],
      discountModeOptions: [
        { value: 'amount', label: '₹' },
        { value: 'percent', label: '%' }
      ],
      form: {
        contact_id: null,
        contact_name: '',
        contact_phone: '',
        contact_phone_2: '',
        contact_address: '',
        contact_pincode: '',
        contact_postofficename: '',
        contact_district: '',
        contact_state: '',
        contact_gstn: '',
        country: 'India',
        city: '',
        shipping_type: 'domestic',
        local_body_name: '',
        local_body_type_name: '',
        ward_no: 0,
        billing_name: '',
        billing_address: '',
        billing_pincode: '',
        billing_postofficename: '',
        billing_district: '',
        billing_state: '',
        order_type: ORDER_TYPE_DEFAULT,
        // Amazon-only identifiers. marketplace_order_id / invoice_number /
        // tracking_id map straight to the like-named order columns;
        // marketplace_invoice_date drives order_date for amazon orders
        // (orderController::store).
        marketplace_order_id: '',
        invoice_number: '',
        tracking_id: '',
        marketplace_invoice_date: '',
        item_count: 0,
        weight: 0,
        total: 0,
        discount: 0,
        total_after_discount: 0,
        shipping_charge: 0,
        cod_fee: 0,
        total_shipping_charge: 0,
        grand_total: 0,
        gst_percentage: 5,
        cgst_amt: 0,
        sgst_amt: 0,
        igst_amt: 0,
        gst_amt: 0,
        gst_state_code: '',
        payment_method: '',
        adjusted_order: '',
        payment_date: '',
        payment_status: 'payment_pending',
        order_status: 'confirmed',
        shipping_method: '',
        shipping_status: 'for_print',
        order_items: []
      },
      // Ids of items removed from an existing order during this edit
      // session. Sent as `deleted_items` on PUT so the backend can drop
      // the corresponding rows and reverse the stock-ledger entries.
      deletedItemIds: []
    }
  },
  computed: {
    selectedDraftItem () {
      return this.itemOptions.find((item) => item.value === this.productDraft.item_id) || null
    },
    productDraftPrice () {
      return Number(this.selectedDraftItem?.price || 0)
    },
    productDraftTotal () {
      return Number(this.productDraftPrice || 0) * Number(this.productDraft.quantity || 0)
    },
    // Sum of qty × per-item shipping weight (g). Posted to cal_cod_charges
    // so the backend uses real catalogue weights for slab selection rather
    // than the legacy 200 g (org 1) / 700 g (org 2) flat assumption, which
    // under-charged any SKU heavier than the flat — e.g. Agro Yield Super
    // 1 kg was being billed at the 51–200 g rate. Falls back to the
    // per-org flat only when neither weight is populated on the catalogue.
    orderTotalWeightGrams () {
      const fallback = Number(this.$store?.state?.activeOrganisationId) === 2 ? 700 : 200
      const byId = new Map(this.itemOptions.map((opt) => [String(opt.value), opt]))
      return this.form.order_items.reduce((sum, line) => {
        const opt = byId.get(String(line.item_id))
        const perItem = Number(opt?.shipping_weight || opt?.weight || 0) || fallback
        return sum + (Number(line.quantity || 0) * perItem)
      }, 0)
    },
    selectedGstState () {
      const stateName = String(this.form.contact_state || '').trim().toLowerCase()
      return this.gstStates.find((state) => String(state.state || '').trim().toLowerCase() === stateName) || null
    },
    amountToPayLabel () {
      return this.formatCurrency(this.amountToPay)
    },
    shippingDistanceLabel () {
      if (this.shippingDistanceKm === null) return ''
      const km = Number(this.shippingDistanceKm)
      if (!Number.isFinite(km) || km <= 0) return ''
      return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`
    },
    // The rate-chart chip. Reads "~123 km" while India Post prices on distance
    // and "Within State" while it prices on zone — whichever the backend
    // actually used for this quote.
    shippingRateChipLabel () {
      if (this.shippingDistanceLabel) return `~${this.shippingDistanceLabel}`
      return this.shippingZoneLabel || ''
    },
    shippingRateChipTitle () {
      if (this.shippingDistanceLabel) {
        return `~${this.shippingDistanceLabel} from warehouse — click to view rate chart`
      }
      return `${this.shippingZoneLabel} delivery zone — click to view rate chart`
    },
    // Hides the COD option from the dropdown when the customer has
    // a prior returned COD on file. paymentMethodOptions stays as
    // the unfiltered source of truth so we don't have to refetch on
    // every customer change.
    visiblePaymentMethods () {
      if (!this.codBlocked) return this.paymentMethodOptions
      return this.paymentMethodOptions.filter((opt) => opt.value !== 'cod')
    },
    // Discount presets — see [src/utils/discountPresets.js] for the
    // resolution rules. Product-scoped tiers stay per-line; org-wide
    // tiers aggregate qty across every line that has no product-scoped
    // tier. itemOptions are already org-scoped by loadBootstrapData, so
    // we don't need to filter the cart for cross-org rows.
    activeDiscountTiers () {
      return (Array.isArray(this.discountPresets) ? this.discountPresets : [])
        .filter((t) => t && Boolean(t.active))
        .slice()
        .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity))
    },
    eligibleDiscountBuckets () {
      const lines = this.form.order_items.map((l) => ({
        item_id: l.item_id,
        quantity: l.quantity,
        subtotal: l.total,
      }))
      return eligibleDiscountBuckets(this.activeDiscountTiers, lines)
    },
    eligibleDiscountTier () {
      // Kept for v-if compatibility: truthy when ANY bucket qualifies.
      return this.eligibleDiscountBuckets[0]?.tier || null
    },
    eligibleDiscountAmount () {
      return eligibleDiscountTotal(this.eligibleDiscountBuckets)
    },
    eligibleDiscountTierLabel () {
      return eligibleDiscountLabel(this.eligibleDiscountBuckets)
    },
    canOverrideCharges () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.some((r) => CHARGE_OVERRIDE_ROLES.includes(r))
    },
    isTelesalesRole () {
      const roles = getStoredRoles().map((r) => String(r).toLowerCase())
      return roles.includes('telesales')
    },
    // Telesales staff must never submit a `telesale` order with ₹0
    // shipping — the most common cause is a missed pincode/post office.
    // Other roles and order types (store_sale, agency, etc.) legitimately
    // ship ₹0, so the rule is scoped here. Empty carts are exempt because
    // the shipping calc only runs once items exist.
    shippingZeroReason () {
      if (!this.isTelesalesRole) return null
      if (this.form.order_type !== 'telesale') return null
      if (!this.form.order_items.length) return null
      if (Number(this.form.shipping_charge || 0) > 0) return null
      if (!this.form.contact_pincode || this.form.contact_pincode.length !== 6) return 'pincode_missing'
      if (!this.form.contact_postofficename) return 'post_office_missing'
      return 'lookup_failed'
    },
    shippingZeroMessage () {
      switch (this.shippingZeroReason) {
        case 'pincode_missing':
          return 'Pincode is missing or incomplete. Shipping is auto-calculated from the pincode — enter a valid 6-digit pincode.'
        case 'post_office_missing':
          return 'Post office is not selected. Pick one from the dropdown so shipping can be calculated.'
        case 'lookup_failed':
          return "Shipping couldn't be auto-calculated for this pincode. Re-enter the pincode or pick a different post office."
        default:
          return ''
      }
    },
    discountFieldsLocked () {
      // Privileged users may type a manual override when the preset
      // toggle is off; everyone else is locked to the toggle path.
      if (this.applyPreset) return true
      return !this.canOverrideCharges
    },
    shippingFieldLocked () {
      // Marketplace (Amazon / Flipkart) orders never carry shipping — the
      // channel handles fulfilment — so the field is forced to 0 and locked
      // for everyone, even charge-override roles.
      if (this.isMarketplaceOrder) return true
      // Shipping has no preset/toggle equivalent — it's either the
      // auto-calc value (COD/IP) or 0. Non-privileged users can't edit
      // it; the auto-calc handler still sets form.shipping_charge
      // programmatically, which :disabled doesn't block.
      return !this.canOverrideCharges
    },
    gstSummaryLabel () {
      return this.form.gst_state_code === 'KL' ? `CGST + SGST (${this.form.gst_percentage}%)` : `IGST (${this.form.gst_percentage}%)`
    },
    dialCode () {
      return dialCodeFor(this.countryCode)
    },
    // Phone-length validity is country-aware (India 10, Saudi/Gulf 8–9;
    // see utils/countries.js `nsn`). Kept in sync with TeleSales.vue —
    // replaces the old hardcoded `=== 10` checks so a pasted +966 number
    // (9 local digits) is accepted.
    phoneValid () {
      return isValidPhoneLength(this.countryCode, this.form.contact_phone)
    },
    altPhoneValid () {
      return isValidPhoneLength(this.countryCode, this.form.contact_phone_2)
    },
    // Full E.164-style number (e.g. "+919876543210") stored on the
    // contact / order rows. The form input itself holds only the local
    // subscriber number (length is country-aware — 10 for India, 9 for
    // Saudi, etc.); the country code is prepended at submit / search time.
    fullContactPhone () {
      return this.form.contact_phone ? `+${this.dialCode}${this.form.contact_phone}` : ''
    },
    fullAlternatePhone () {
      return this.form.contact_phone_2 ? `+${this.dialCode}${this.form.contact_phone_2}` : ''
    },
    submitConfirmMessage () {
      const name = this.form.contact_name || 'this customer'
      const phone = this.form.contact_phone ? ` (+${this.dialCode} ${this.form.contact_phone})` : ''
      const items = this.form.order_items.length
      const total = this.formatCurrency(this.form.grand_total)
      if (this.editOrderId) {
        return `${name}${phone} · ${items} item${items === 1 ? '' : 's'} · ${total}. The order will be updated with the new details.`
      }
      return `${name}${phone} · ${items} item${items === 1 ? '' : 's'} · ${total}. Once saved, the order is created and stock is committed.`
    },
    allCrops () {
      return [...this.contactCrops, ...this.existingCrops]
    },
    // Customer Crops is only meaningful for the agro/crops org (id 1)
    // — every other org's order form hides the section. Gates the
    // section rendering AND the crops_data submission, so non-org-1
    // orders never write to contact_has_crops (and don't trigger the
    // backend's wipe-on-save). Tied to `formOrganisationId` instead of
    // `activeOrganisationId` so edit-mode follows the ORDER's org, not
    // the current session. Matches TeleSales' showCropsSection gate.
    showCropsSection () {
      return this.formOrganisationId === 1
    },
    postOfficeDropdownOptions () {
      return this.postOfficeOptions.map((name) => ({ value: name, label: name }))
    },
    localBodyDropdownOptions () {
      return this.localBodyOptions.map((body) => ({
        value: body.local_body_name,
        label: body.local_body_name
      }))
    },
    isFormReady () {
      const hasItems = this.form.order_items.length > 0
      const hasCore = Boolean(this.form.contact_name && this.form.contact_phone && this.form.contact_address && this.form.order_type && this.form.payment_method && this.form.shipping_method)
      const hasDomesticAddress = this.form.shipping_type !== 'domestic' || Boolean(this.form.contact_pincode && this.form.contact_postofficename && this.form.contact_district && this.form.contact_state)
      // Alt phone is required to gate Save/Update — the backend rule
      // exempts store_sale, but the UI keeps it strict to match the
      // TeleSales flow and reduce return-to-origin shipments.
      const hasAltPhone = this.altPhoneValid
      // Marketplace (Amazon/Flipkart) orders need their identifiers — and
      // the backend reads marketplace_invoice_date directly (it becomes
      // order_date), so a missing date would 500 the save. Gate on both here.
      const marketplaceReady = !this.isMarketplaceOrder || Boolean(this.form.marketplace_order_id && this.form.marketplace_invoice_date)
      return hasItems && hasCore && hasDomesticAddress && hasAltPhone && marketplaceReady
    },
    // True when the order is billed through a marketplace channel
    // (Amazon/Flipkart) — drives the toolbar toggles, the swapped
    // identifier fields, and the forced placeholder phone.
    isMarketplaceOrder () {
      return MARKETPLACE_ORDER_TYPES.includes(this.marketplaceMode)
    },
    marketplaceLabel () {
      return MARKETPLACE_LABELS[this.marketplaceMode] || ''
    },
    marketplaceOrderIdPlaceholder () {
      return MARKETPLACE_ORDER_ID_PLACEHOLDERS[this.marketplaceMode] || ''
    },
    searchHintClass () {
      if (!this.searchHint) return 'text-muted'
      if (this.searchHint.toLowerCase().includes('found')) return 'text-success'
      if (this.searchHint.toLowerCase().includes('not')) return 'text-warning'
      return 'text-muted'
    },
    hasContactHistory () {
      return this.contactCalls.length > 0 || this.contactOrders.length > 0
    },
    lifetimeSpend () {
      return this.contactOrders.reduce((sum, order) => sum + Number(order.grand_total || 0), 0)
    },
    lastCallSummary () {
      if (!this.contactCalls.length) return '—'
      return this.contactCalls[0].status || '—'
    },
    historyCondensedSummary () {
      const callCount = this.contactCalls.length
      const orderCount = this.contactOrders.length
      const parts = [`${callCount} call${callCount === 1 ? '' : 's'}`, `${orderCount} order${orderCount === 1 ? '' : 's'}`]
      if (orderCount > 0) parts.push(this.formatCurrency(this.lifetimeSpend))
      return parts.join(' · ')
    },
    pageTitle () {
      if (this.editOrderId) {
        return this.editOrderNumber
          ? `Edit Order ${this.editOrderNumber}`
          : `Edit Order #${this.editOrderId}`
      }
      return 'New Order'
    }
  },
  created () {
    const rawId = Number(this.$route.query.order_id)
    if (Number.isInteger(rawId) && rawId > 0) {
      this.editOrderId = rawId
      this.editOrderNumber = String(this.$route.query.order_number || '')
      // Edit mode: fetch the order shell first so we know which org it
      // belongs to, then bootstrap with ?organisation_id=X. This makes the
      // items dropdown match the ORDER's org instead of the user's active
      // org (an admin sitting on MAP editing an MDI order would otherwise
      // see MAP items in the catalogue).
      this.bootstrapForEdit(rawId)
      return
    }
    this.loadBootstrapData()
  },
  methods: {
    normalizeDropdown (entries) {
      return (entries || []).map((entry) => ({
        value: String(entry.slug || entry.value || entry.id || ''),
        label: entry.name || entry.text || entry.slug || ''
      })).filter((entry) => entry.value)
    },
    formatCallDate (date) {
      if (!date) return ''
      const parts = String(date).split('-')
      if (parts.length !== 3) return String(date)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${Number(parts[0])} ${months[Number(parts[1]) - 1] || parts[1]} ${parts[2]}`
    },
    formatOrderDate (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return String(iso)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`
    },
    formatLabel (value) {
      return String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },
    callStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s.includes('sold') || s === 'closed') return 'is-success'
      if (s.includes('not interested')) return 'is-muted'
      if (s.includes('not attended')) return 'is-warn'
      if (s.includes('feedback') || s.includes('follow')) return 'is-info'
      return 'is-default'
    },
    paymentStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s === 'paid') return 'text-success'
      if (s.includes('pending')) return 'text-warning'
      return ''
    },
    formatCurrency (value) {
      const amount = Number(value || 0)
      return Number.isFinite(amount) ? `₹${amount.toFixed(2)}` : '₹0.00'
    },
    toggleGstin (checked) {
      this.includeGstin = Boolean(checked)
      if (!this.includeGstin) {
        this.form.contact_gstn = ''
        this.form.gst_percentage = 0
      } else {
        this.form.gst_percentage = 5
        // Prefill from the searched customer's stored GSTIN, but only when
        // staff opt in by ticking the box and the field is still empty.
        if (!this.form.contact_gstn && this.contactGstinOnFile) {
          this.form.contact_gstn = this.contactGstinOnFile
        }
      }
      this.recalculateTotals()
    },
    toggleBilling (checked) {
      this.addBillingAddress = Boolean(checked)
      if (!this.addBillingAddress) {
        this.form.billing_name = ''
        this.form.billing_address = ''
        this.form.billing_pincode = ''
        this.form.billing_postofficename = ''
        this.form.billing_district = ''
        this.form.billing_state = ''
      }
    },
    setPhone (value) {
      const next = this.normalizePhoneInput(value, { syncCountry: true })
      // Customer is changing — a stale codBlocked from the previous
      // lookup must not gate the new customer's options. The flag
      // re-resolves when searchContact runs against the new number.
      if (next !== this.form.contact_phone) {
        this.codBlocked = false
      }
      this.form.contact_phone = next
    },
    setAlternatePhone (value) {
      // Peel any country-code prefix on the alternate phone too, but
      // don't sync the country dropdown from it — the primary phone
      // is the canonical source of the customer's country, and a
      // stray paste here (e.g. an Indian alt for a Saudi customer)
      // shouldn't silently flip the whole form's country.
      this.form.contact_phone_2 = this.normalizePhoneInput(value, { syncCountry: false })
    },
    normalizePhoneInput (value, { syncCountry = false } = {}) {
      // Strip non-digits, then handle pasted numbers that include a
      // country-code prefix (e.g. "+919207948134", "+966500000000").
      // Peel the matching dial code so the field shows only the
      // local subscriber number, and — when syncCountry is on —
      // switch the country dropdown to match so staff don't have to
      // re-select it manually. Kept in sync with TeleSales.vue.
      //
      // Trigger conditions: explicit "+" prefix in the raw input
      // (clear paste signal) OR more digits than the selected country's
      // local number can hold (a typed local number can't exceed that,
      // so the excess is almost always a pasted country code). Falls
      // back to the last `maxLen` digits when no known country code
      // matches, preserving recovery for common malformed pastes
      // (0-leader, ISD code, etc.). `maxLen` is country-aware — 10 for
      // India, 9 for Saudi (+966), etc. — so Gulf numbers aren't
      // truncated to an Indian-sized 10.
      const raw = String(value || '')
      let digits = raw.replace(/\D/g, '')

      let maxLen = maxPhoneLengthFor(this.countryCode)
      const shouldPeel = digits.length > maxLen || raw.trim().startsWith('+')

      if (shouldPeel) {
        const match = findCountryByDialCode(digits)
        if (match) {
          const codeDigits = match.code.replace(/^\+/, '')
          digits = digits.slice(codeDigits.length)
          // Re-cap to the matched country's local length so the peeled
          // subscriber number isn't clipped to the old country's size.
          maxLen = maxPhoneLengthFor(match.name)
          if (syncCountry && this.countryCode !== match.name) {
            this.countryCode = match.name
          }
        } else if (digits.length > maxLen) {
          digits = digits.slice(-maxLen)
        }
      }

      return digits.slice(0, maxLen)
    },
    buildWhatsappShareMessage () {
      const today = new Date()
      const dd = String(today.getDate()).padStart(2, '0')
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const yyyy = today.getFullYear()
      const date = `${dd}/${mm}/${yyyy}`

      // Logged-in user's display name — same source TheHeader uses
      // for the "Good evening, …" greeting.
      let caller = ''
      try {
        const raw = localStorage.getItem('auth_user')
        const user = raw ? JSON.parse(raw) : null
        if (user && typeof user.name === 'string') {
          caller = user.name.trim().toUpperCase()
        }
      } catch (e) { /* localStorage corrupt — leave blank */ }

      const customerName = String(this.form.contact_name || '').trim().toUpperCase()
      const phone = this.form.contact_phone ? `+${this.dialCode}${this.form.contact_phone}` : ''

      const addressParts = [
        this.form.contact_address,
        this.form.contact_postofficename,
        this.form.contact_district,
        this.form.contact_state
      ].map((part) => String(part || '').trim()).filter(Boolean)
      const pincode = this.form.contact_pincode ? String(this.form.contact_pincode).trim() : ''
      const address = (pincode ? [...addressParts, pincode] : addressParts).join(', ').toUpperCase()

      const totalQty = this.form.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
      const productNames = this.form.order_items
        .map((item) => String(item.name || '').trim())
        .filter(Boolean)
        .join('\n')

      const paymentMethod = this.form.payment_method ? this.formatLabel(this.form.payment_method) : ''
      const shippingMethod = this.form.shipping_method ? this.formatLabel(this.form.shipping_method) : ''

      return [
        `Date : ${date}`,
        `Tele callar : ${caller}`,
        '',
        '',
        'Customer Name:',
        customerName,
        'Customer Phone:',
        phone,
        'Customer Address:',
        address,
        `Product quantity ${totalQty}`,
        '',
        'Product name :',
        productNames,
        '',
        `Payment method. ${paymentMethod}`,
        `Shipping method. ${shippingMethod}`
      ].join('\n')
    },
    async copyAndShareWhatsapp () {
      if (!this.form.contact_phone) return

      const message = this.buildWhatsappShareMessage()
      this.errorMessage = ''
      this.successMessage = ''

      // Modern Clipboard API can fail on http or with denied
      // permission — fall through to opening WhatsApp regardless so
      // the share still happens even if the copy didn't.
      let copied = false
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(message)
          copied = true
        }
      } catch (e) {
        copied = false
      }

      this.successMessage = copied
        ? 'Details copied to clipboard. WhatsApp opened — pick a contact to send.'
        : 'WhatsApp opened — pick a contact to send.'

      // wa.me without a phone opens WhatsApp's share-target picker so
      // the agent can forward to a colleague or the customer.
      const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(waUrl, '_blank', 'noopener')
    },
    loadBootstrapData (organisationId = null) {
      this.isLoading = true
      this.errorMessage = ''

      // Resolve the org the form is operating under so we can fetch
      // its discount tiers AND scope the items catalogue. Edit mode
      // passes the order's org explicitly; new orders fall back to
      // the user's active org from the store. We send organisation_id
      // even on new orders so the backend doesn't race the in-flight
      // change_my_org POST and return the previous org's products.
      const resolvedOrgId = organisationId
        || Number(this.$store?.state?.activeOrganisationId)
        || null
      this.loadDiscountPresets(resolvedOrgId)

      const params = {}
      if (resolvedOrgId) {
        params.organisation_id = resolvedOrgId
      }

      return axios.get(`${this.$apiAdress}/api/order_form_data`, { params, timeout: 15000 })
        .then((response) => {
          const data = response.data || {}
          const dropdown = data.drop_down_items || {}

          this.orderTypeOptions = this.normalizeDropdown(dropdown.order_type)
          this.paymentMethodOptions = this.normalizeDropdown(dropdown.payment_method)
          this.shippingMethodOptions = this.normalizeDropdown(dropdown.shipping_method)
            .filter((opt) => opt.value !== 'ip_registered_parcel')

          const items = Array.isArray(data.items) ? data.items : []
          this.itemOptions = items.map((item) => ({
            value: String(item.id),
            id: Number(item.id),
            label: item.sku ? `${item.name} (${item.sku})` : item.name,
            name: item.name,
            sku: item.sku || '',
            shortcode: item.shortcode || '',
            price: Number(item.selling_price ?? item.price ?? item.sale_price ?? item.rate ?? item.unit_price ?? item.mrp ?? 0),
            weight: Number(item.weight || 0),
            shipping_weight: Number(item.shipping_weight || 0)
          }))

          const gstStateGroups = data.gst_states || {}
          const groupedValues = Array.isArray(gstStateGroups)
            ? gstStateGroups
            : Object.values(gstStateGroups)
          this.gstStates = groupedValues.flatMap((rows) => (Array.isArray(rows) ? rows : []))

          this.cropOptions = (Array.isArray(data.crops) ? data.crops : []).map((crop) => crop.name).filter(Boolean)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Unable to load order form data.'
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    reloadCurrent () {
      // In edit mode, redo the org-scoped bootstrap so the items dropdown
      // doesn't silently revert to the user's active org on refresh.
      if (this.editOrderId) {
        this.bootstrapForEdit(this.editOrderId)
        return
      }
      this.loadBootstrapData()
    },
    bootstrapForEdit (id) {
      this.isLoadingExisting = true
      this.errorMessage = ''

      axios.get(`${this.$apiAdress}/api/orders/${id}`, { timeout: 15000 })
        .then((response) => {
          // The resource controller's show() returns a single-row array; the
          // older shape was a bare model. Normalise to one record.
          let order = response.data
          if (Array.isArray(order)) order = order[0]
          if (!order) {
            this.errorMessage = `Order #${id} not found.`
            return null
          }
          // Bootstrap with the order's organisation_id so the items
          // catalogue matches what was sold, then apply the order. We
          // chain on the bootstrap promise so applyExistingOrder runs
          // ONLY after the dropdown / itemOptions are populated.
          const orgId = Number(order.organisation_id) || null
          return this.loadBootstrapData(orgId).then(() => {
            this.applyExistingOrder(order)
          })
        })
        .catch((error) => {
          console.log(error)
          if (!this.errorMessage) {
            this.errorMessage = error?.response?.data?.message || 'Unable to load this order.'
          }
        })
        .finally(() => {
          this.isLoadingExisting = false
        })
    },
    applyExistingOrder (order) {
      // Surface the order number for the page title even if the URL was
      // missing it (e.g. opened from a bookmarked /orderform?order_id=…).
      if (!this.editOrderNumber && order.order_number) {
        this.editOrderNumber = String(order.order_number)
      }

      // Stored phones may include the country code (e.g. "+919876543210"
      // for orders saved after the country-code rollout). The input only
      // holds the local subscriber number, so peel any dial-code prefix.
      // The country itself is restored from order.country first so the
      // peel/cap below is sized to the right country (10 for India, 9
      // for Saudi, etc.).
      const storedCountry = findCountryByName(order.country || '')
      if (storedCountry) {
        this.countryCode = storedCountry.name
      }
      const last10 = (value) => this.normalizePhoneInput(value, { syncCountry: false })

      const items = Array.isArray(order.order_items) ? order.order_items : []
      this.form = {
        contact_id: order.contact_id || null,
        contact_name: order.contact_name || '',
        contact_phone: last10(order.contact_phone),
        contact_phone_2: last10(order.contact_phone_2 || order.contact_phone),
        contact_address: order.contact_address || '',
        contact_pincode: order.contact_pincode || '',
        contact_postofficename: order.contact_postofficename || '',
        contact_district: order.contact_district || '',
        contact_state: order.contact_state || '',
        contact_gstn: order.contact_gstn || '',
        country: order.country || 'India',
        city: order.city || '',
        shipping_type: order.shipping_type || 'domestic',
        local_body_name: order.local_body_name || '',
        local_body_type_name: order.local_body_type_name || '',
        ward_no: Number(order.ward_no || 0),
        billing_name: order.billing_name || '',
        billing_address: order.billing_address || '',
        billing_pincode: order.billing_pincode || '',
        billing_postofficename: order.billing_postofficename || '',
        billing_district: order.billing_district || '',
        billing_state: order.billing_state || '',
        order_type: order.order_type || ORDER_TYPE_DEFAULT,
        marketplace_order_id: order.marketplace_order_id || '',
        invoice_number: order.invoice_number || '',
        tracking_id: order.tracking_id || '',
        // For amazon orders order_date IS the invoice date — surface it back
        // in the Invoice Date field on edit.
        marketplace_invoice_date: order.order_date ? String(order.order_date).slice(0, 10) : '',
        item_count: Number(order.item_count || 0),
        weight: Number(order.weight || 0),
        total: Number(order.total || 0),
        discount: Number(order.discount || 0),
        total_after_discount: Number(order.total_after_discount || 0),
        shipping_charge: Number(order.shipping_charge || 0) + Number(order.cod_charge || 0),
        total_shipping_charge: Number(order.total_shipping_charge || 0),
        grand_total: Number(order.grand_total || 0),
        gst_percentage: Number(order.gst_percentage || 0),
        cgst_amt: Number(order.cgst_amt || 0),
        sgst_amt: Number(order.sgst_amt || 0),
        igst_amt: Number(order.igst_amt || 0),
        gst_amt: Number(order.gst_amt || 0),
        gst_state_code: order.gst_state_code || '',
        payment_method: order.payment_method || '',
        adjusted_order: order.adjusted_order || '',
        payment_date: order.payment_date ? String(order.payment_date).slice(0, 10) : '',
        payment_status: order.payment_status || 'payment_pending',
        order_status: order.order_status || 'confirmed',
        shipping_method: order.shipping_method || '',
        shipping_status: order.shipping_status || 'for_print',
        order_items: items.map((item) => ({
          // Preserve the existing row id so submitOrder can mark it as an
          // UPDATE; without this, the backend treats every row as new and
          // duplicates the entire item set on save.
          id: item.id || null,
          item_id: String(item.item_id || ''),
          name: item.name || '',
          sku: item.sku || '',
          shortcode: item.shortcode || '',
          quantity: Number(item.quantity || 0),
          price: Number(item.price || 0),
          total: Number(item.total || (Number(item.quantity || 0) * Number(item.price || 0)))
        }))
      }

      // Reset the tombstone list whenever we (re)hydrate from the server so
      // stale ids from a previous edit can't accidentally delete real items.
      this.deletedItemIds = []

      // Reflect the persisted values on the optional-block toggles so the
      // related sections render and editing them stays consistent with how
      // a fresh order is created.
      this.includeGstin = Boolean(order.contact_gstn)
      this.addBillingAddress = Boolean(order.billing_name || order.billing_address)
      // Editing an existing marketplace order surfaces the matching mode UI.
      this.marketplaceMode = MARKETPLACE_ORDER_TYPES.includes(this.form.order_type) ? this.form.order_type : ''

      // Match the discount control to whichever shape the order was saved
      // with (raw amount vs. percentage). Default to amount if neither matches.
      if (Number(order.discount_percentage || 0) > 0) {
        this.discountMode = 'percent'
        this.discountInput = Number(order.discount_percentage)
      } else {
        this.discountMode = 'amount'
        this.discountInput = Number(order.discount || 0)
      }

      // Edit-mode COD-block resolution. searchContact() would clobber
      // the snapshotted address with the customer's latest-address,
      // so we fetch the flag in isolation. Backend update() guards
      // the save either way; this just surfaces the banner sooner.
      this.refreshCodBlockedFromPhone()
    },
    refreshCodBlockedFromPhone () {
      if (!this.phoneValid) return
      const lookup = encodeURIComponent(this.fullContactPhone)
      axios.get(`${this.$apiAdress}/api/contacts/phone/${lookup}?cross_org=1`, { timeout: 15000 })
        .then((response) => {
          this.codBlocked = Boolean(response.data?.cod_blocked)
        })
        .catch(() => { /* soft-fail: backend submit still enforces */ })
    },
    searchContact () {
      if (!this.phoneValid) {
        this.searchHint = 'Enter a valid phone number.'
        return
      }

      this.isSearching = true
      this.searchHint = 'Searching contact...'

      axios.get(`${this.$apiAdress}/api/contacts/phone/${encodeURIComponent(this.fullContactPhone)}?cross_org=1`, { timeout: 15000 })
        .then((response) => {
          const data = response.data || {}
          const contact = Array.isArray(data.contact) ? data.contact[0] : null
          const orders = Array.isArray(data.orders) ? data.orders : []
          this.contactCrops = this.normalizeCropList(data.crops)
          this.contactCalls = Array.isArray(data.calls) ? data.calls : []
          this.contactOrders = orders
          this.codBlocked = Boolean(data.cod_blocked)
          // If staff had already picked COD before searching (or
          // hydrated from an old draft), snap the selection off so
          // the form can't submit something the backend will reject.
          // loadChargeEstimate runs inside onPaymentOrShippingChange
          // to clear the now-stale COD fee.
          if (this.codBlocked && this.form.payment_method === 'cod') {
            this.form.payment_method = ''
            this.onPaymentOrShippingChange()
          }
          this.historyCollapsed = false

          if (!contact) {
            this.searchHint = 'Contact not found.'
            return
          }

          this.form.contact_id = contact.id || null
          this.form.contact_name = contact.name || this.form.contact_name

          const addresses = Array.isArray(contact.address) ? contact.address : []
          const primaryAddress = addresses.find((address) => Number(address.pincode || 0) > 0) || addresses[0] || null
          const latestOrder = orders[0] || null

          // Resolve each address field with the same primary→latestOrder
          // fallback TeleSales uses, so contacts that have no addresses
          // row (or stale/empty rows) still hydrate from the last order.
          const resolvedAddress = (primaryAddress && primaryAddress.address) || (latestOrder && latestOrder.contact_address) || ''
          const resolvedPincode = (primaryAddress && primaryAddress.pincode) || (latestOrder && latestOrder.contact_pincode) || ''
          const resolvedPostOffice = (primaryAddress && primaryAddress.post_office_name) || (latestOrder && latestOrder.contact_postofficename) || ''
          const resolvedDistrict = (primaryAddress && primaryAddress.district) || (latestOrder && latestOrder.contact_district) || ''
          const resolvedState = (primaryAddress && primaryAddress.state) || (latestOrder && latestOrder.contact_state) || ''
          const resolvedCountry = (primaryAddress && primaryAddress.country) || (latestOrder && latestOrder.country) || ''
          const resolvedLocalBody = (primaryAddress && primaryAddress.local_body_name) || (latestOrder && latestOrder.local_body_name) || ''
          const resolvedLocalBodyType = (primaryAddress && primaryAddress.local_body_type_name) || (latestOrder && latestOrder.local_body_type_name) || ''
          const resolvedWardNo = (primaryAddress && primaryAddress.ward_no) || (latestOrder && latestOrder.ward_no) || 0
          const resolvedGstin = (primaryAddress && primaryAddress.gstin) || (latestOrder && latestOrder.contact_gstn) || ''

          // Remember the customer's on-file GSTIN, but never auto-enable the
          // toggle from it — auto-filling silently flipped orders to B2B. We
          // only prefill the field when staff have already ticked the box;
          // toggleGstin() handles the tick-after-search case.
          this.contactGstinOnFile = resolvedGstin

          if (resolvedAddress || resolvedPincode || resolvedPostOffice) {
            this.form.contact_address = resolvedAddress || this.form.contact_address
            this.form.contact_pincode = resolvedPincode ? String(resolvedPincode) : this.form.contact_pincode
            this.form.contact_postofficename = resolvedPostOffice || this.form.contact_postofficename
            this.form.contact_district = resolvedDistrict || this.form.contact_district
            this.form.contact_state = resolvedState || this.form.contact_state
            this.form.country = resolvedCountry || this.form.country
            this.form.local_body_name = resolvedLocalBody || this.form.local_body_name
            this.form.local_body_type_name = resolvedLocalBodyType || this.form.local_body_type_name
            this.form.ward_no = Number(resolvedWardNo || 0)
            if (this.includeGstin && resolvedGstin) {
              this.form.contact_gstn = resolvedGstin
            }
            this.selectedLocalBodyName = this.form.local_body_name
          }

          // Alternate phone prefill — newer orders.contact_phone_2
          // column wins when present, else fall back to the
          // historical addresses.address_phone.
          const rawAltPhone = (latestOrder && latestOrder.contact_phone_2)
            || (primaryAddress && primaryAddress.address_phone)
            || ''
          // Peel any stored country-code prefix and cap to the local
          // length (country-aware) rather than blindly keeping the last
          // 10 digits, which would mangle a sub-10-digit Gulf number.
          const resolvedAltPhone = this.normalizePhoneInput(rawAltPhone, { syncCountry: false })
          if (resolvedAltPhone) {
            this.form.contact_phone_2 = resolvedAltPhone
          }

          this.searchHint = 'Contact found.'

          if (this.form.contact_pincode.length === 6) {
            this.searchPincode(true)
          }
        })
        .catch((error) => {
          console.log(error)
          this.searchHint = 'Unable to fetch contact details.'
        })
        .finally(() => {
          this.isSearching = false
          this.recalculateTotals()
        })
    },
    normalizeCropList (crops) {
      if (!Array.isArray(crops)) return []
      // API rows come from ContactHasCrops with the `crop` relation
      // eager-loaded as an object ({id, name, …}). `area` is stored
      // normalised to m²; reverse-convert it back to the persisted unit
      // so the table shows the area in the unit the customer entered.
      // Rows from before the `unit` column existed have unit === null
      // and stay displayed in m² (their canonical form).
      const toUnit = { cent: 40.46856, acre: 4046.856, hectare: 10000, metersquare: 1 }
      return crops.map((crop) => {
        const cropName = (crop.crop && typeof crop.crop === 'object')
          ? (crop.crop.name || '')
          : (crop.crop || crop.name || crop.crop_name || '')
        const unit = crop.unit || 'metersquare'
        const divisor = toUnit[unit] || 1
        return {
          crop: cropName,
          area: Number(crop.area || 0) / divisor,
          unit,
          count: Number(crop.count || 0)
        }
      }).filter((crop) => crop.crop)
    },
    formatCropUnit (value) {
      const labels = { cent: 'Cent', acre: 'Acre', hectare: 'Hectare', metersquare: 'm²' }
      return labels[value] || value
    },
    onPincodeInput (value) {
      const next = String(value || '').replace(/\D/g, '').slice(0, 6)
      const changed = next !== this.form.contact_pincode
      this.form.contact_pincode = next
      if (next.length < 6) {
        this.postOfficeRecords = []
        this.postOfficeOptions = []
        return
      }
      if (changed && !this.isPincodeLoading) {
        this.searchPincode(true)
      }
    },
    searchPincode (loadLocalBodies = true) {
      if (this.form.contact_pincode.length !== 6) return

      this.isPincodeLoading = true
      axios.get(`${this.$apiAdress}/api/pincodes/${this.form.contact_pincode}`, { timeout: 15000 })
        .then((response) => {
          const results = Array.isArray(response.data) ? response.data : []
          this.postOfficeRecords = results
          this.postOfficeOptions = [...new Set(results.map((row) => row.OfficeName).filter(Boolean))]

          if (this.postOfficeOptions.length > 0 && !this.postOfficeOptions.includes(this.form.contact_postofficename)) {
            this.form.contact_postofficename = this.postOfficeOptions[0]
          }

          this.applyPostOfficeDetails()

          if (loadLocalBodies) {
            this.loadDistrictLocalBodies()
          }

          this.loadChargeEstimate()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = 'Pincode lookup failed.'
          this.postOfficeRecords = []
          this.postOfficeOptions = []
        })
        .finally(() => {
          this.isPincodeLoading = false
        })
    },
    onPostOfficeSelect (value) {
      this.form.contact_postofficename = value
      this.applyPostOfficeDetails()
      this.loadDistrictLocalBodies()
      this.loadChargeEstimate()
    },
    applyPostOfficeDetails () {
      if (!this.postOfficeRecords.length) return
      const selected = this.postOfficeRecords.find((row) => row.OfficeName === this.form.contact_postofficename) || this.postOfficeRecords[0]
      this.form.contact_postofficename = selected?.OfficeName || ''
      this.form.contact_pincode = String(selected?.Pincode || this.form.contact_pincode || '')
      this.form.contact_district = selected?.District || this.form.contact_district
      this.form.contact_state = selected?.StateName || this.form.contact_state
      this.form.country = 'India'
      this.form.shipping_type = 'domestic'
      this.recalculateTotals()
    },
    loadDistrictLocalBodies () {
      const district = String(this.form.contact_district || '').trim()
      if (!district) return
      axios.get(`${this.$apiAdress}/api/address/local_bodies/${encodeURIComponent(district)}`, { timeout: 15000 })
        .then((response) => {
          const names = Array.isArray(response.data) ? response.data.filter(Boolean) : []
          this.localBodyOptions = [...new Set(names)].map((name) => ({
            local_body_name: name,
            local_body_type_name: this.form.local_body_type_name || ''
          }))
          if (this.form.local_body_name && !names.includes(this.form.local_body_name)) {
            this.form.local_body_name = ''
            this.selectedLocalBodyName = ''
          }
        })
        .catch((error) => {
          console.log(error)
        })
    },
    onLocalBodySelect (value) {
      this.selectedLocalBodyName = value
      const selected = this.localBodyOptions.find((row) => row.local_body_name === value)
      if (!selected) {
        this.form.local_body_name = ''
        this.form.local_body_type_name = ''
        return
      }
      this.form.local_body_name = selected.local_body_name
      this.form.local_body_type_name = selected.local_body_type_name || ''
    },
    addCropInfo () {
      if (!this.cropDraft.crop) return
      this.existingCrops.push({
        crop: this.cropDraft.crop,
        area: Number(this.cropDraft.area || 0),
        unit: this.cropDraft.unit,
        count: Number(this.cropDraft.count || 0)
      })
      this.cropDraft = {
        crop: '',
        area: 0,
        unit: 'cent',
        count: 0
      }
    },
    onDraftProductSelect (value) {
      this.productDraft.item_id = value
      if (!this.selectedDraftItem) {
        this.productDraft.quantity = 1
      }
    },
    addSelectedProduct () {
      const product = this.selectedDraftItem
      const quantity = Math.max(1, Number(this.productDraft.quantity || 1))
      if (!product) return

      const existing = this.form.order_items.find((item) => Number(item.item_id) === Number(product.id))
      if (existing) {
        existing.quantity += quantity
        existing.total = Number(existing.quantity) * Number(existing.price)
      } else {
        this.form.order_items.push({
          item_id: Number(product.id),
          name: product.name,
          sku: product.sku,
          shortcode: product.shortcode,
          quantity,
          price: Number(product.price),
          total: Number(product.price) * quantity
        })
      }

      this.productDraft.quantity = 1
      this.recalculateTotals()
      this.loadChargeEstimate()
    },
    updateItemQuantity (index, value) {
      const qty = Math.max(1, Number(value || 1))
      this.form.order_items[index].quantity = qty
      this.form.order_items[index].total = qty * Number(this.form.order_items[index].price || 0)
      this.recalculateTotals()
      this.loadChargeEstimate()
    },
    removeItem (index) {
      const [removed] = this.form.order_items.splice(index, 1)
      // Only persisted rows carry an id; freshly-added draft items can
      // just be dropped from the in-memory list.
      if (removed && removed.id) {
        this.deletedItemIds.push(removed.id)
      }
      this.recalculateTotals()
      this.loadChargeEstimate()
    },
    onDiscountModeChange (value) {
      this.discountMode = value
      this.recalculateTotals()
    },
    onDiscountInput (value) {
      this.discountInput = Number(value || 0)
      this.recalculateTotals()
    },
    onApplyPresetToggle (checked) {
      // User-driven engage/disengage. recalculateTotals owns the math
      // (and the auto-disengage when qty drops below all tiers), so we
      // just flip the flag and let it propagate. Disengaging zeroes
      // the manual fields here to avoid stale values lingering after
      // the lock is removed.
      this.applyPreset = Boolean(checked)
      if (!this.applyPreset) {
        this.discountMode = 'amount'
        this.discountInput = 0
      }
      this.recalculateTotals()
    },
    loadDiscountPresets (organisationId) {
      const orgId = Number(organisationId) || null
      this.formOrganisationId = orgId
      if (!orgId) {
        this.discountPresets = []
        return Promise.resolve([])
      }
      return axios.get(`${this.$apiAdress}/api/discount-presets/by-org/${orgId}`, { timeout: 10000 })
        .then((response) => {
          this.discountPresets = Array.isArray(response.data) ? response.data : []
          return this.discountPresets
        })
        .catch(() => {
          // Soft-fail: a missing preset list shouldn't block the form.
          // It just means the toggle never appears for this org.
          this.discountPresets = []
          return []
        })
    },
    onShippingInput (value) {
      this.form.shipping_charge = Number(value || 0)
      this.recalculateTotals()
    },
    onPaymentMethodSelect (value) {
      this.form.payment_method = value
      this.onPaymentOrShippingChange()
    },
    onShippingMethodSelect (value) {
      this.form.shipping_method = value
      this.onPaymentOrShippingChange()
    },
    onPaymentOrShippingChange () {
      if (this.form.payment_method === 'cod') {
        this.form.payment_status = 'payment_pending'
      }
      this.loadChargeEstimate()
      this.recalculateTotals()
    },
    onOrderTypeSelect (value) {
      this.form.order_type = value
      // Keep the toolbar toggles and the order-type dropdown as two views
      // of the same state — picking 'amazon'/'flipkart' here flips the
      // matching marketplace mode on, and picking anything else flips it off.
      this.syncMarketplaceMode()
    },
    toggleMarketplaceMode (type, checked) {
      // The two toolbar toggles are mutually exclusive — order_type is a
      // single value, so turning one on (or off) simply rewrites it.
      this.form.order_type = checked ? type : ORDER_TYPE_DEFAULT
      this.syncMarketplaceMode()
    },
    syncMarketplaceMode () {
      this.marketplaceMode = MARKETPLACE_ORDER_TYPES.includes(this.form.order_type) ? this.form.order_type : ''
      if (this.marketplaceMode) {
        this.applyMarketplaceDefaults(this.marketplaceMode)
      } else {
        this.clearMarketplaceForcedPhone()
      }
    },
    applyMarketplaceDefaults (type) {
      // Phone is billed under a fixed placeholder; there's no customer to
      // look up, so drop any stale contact-history / COD-block state.
      this.form.contact_phone = MARKETPLACE_PLACEHOLDER_PHONE
      this.form.contact_phone_2 = MARKETPLACE_PLACEHOLDER_PHONE
      this.form.shipping_status = 'for_picking'
      this.codBlocked = false
      this.searchHint = ''
      this.contactCalls = []
      this.contactOrders = []
      // A marketplace order is always fulfilled and settled through that
      // channel, so pre-pick the matching payment + shipping methods to save
      // the biller the two redundant selections. onPaymentOrShippingChange
      // refreshes the charge estimate / totals for the new methods.
      this.form.payment_method = type
      this.form.shipping_method = type
      this.onPaymentOrShippingChange()
    },
    clearMarketplaceForcedPhone () {
      // Only wipe the phones if they still hold the placeholder — never
      // clobber a real number the user typed before toggling off.
      if (this.form.contact_phone === MARKETPLACE_PLACEHOLDER_PHONE) this.form.contact_phone = ''
      if (this.form.contact_phone_2 === MARKETPLACE_PLACEHOLDER_PHONE) this.form.contact_phone_2 = ''
      // Drop the auto-applied marketplace methods so the user picks fresh
      // ones for the now-regular order; leave any other method intact.
      if (MARKETPLACE_ORDER_TYPES.includes(this.form.payment_method)) this.form.payment_method = ''
      if (MARKETPLACE_ORDER_TYPES.includes(this.form.shipping_method)) this.form.shipping_method = ''
    },
    // Wipes the "why is shipping this much" context. Called wherever the
    // charge is zeroed or the estimate fails, so a stale zone / distance chip
    // can never outlive the quote it came from.
    clearShippingRateContext () {
      this.shippingDistanceKm = null
      this.shippingZone = null
      this.shippingZoneLabel = ''
    },
    applyShippingRateContext (data) {
      const dist = Number(data?.distance_km)
      this.shippingDistanceKm = Number.isFinite(dist) && dist > 0 ? dist : null
      this.shippingZone = data?.zone || null
      this.shippingZoneLabel = data?.zone_label || ''
    },
    loadChargeEstimate () {
      // Marketplace (Amazon / Flipkart) orders are fulfilled and settled
      // through the channel — the seller never bills shipping, so force the
      // charge to 0 and skip the COD-charge estimate entirely.
      if (this.isMarketplaceOrder) {
        this.form.shipping_charge = 0
        this.form.cod_fee = 0
        this.clearShippingRateContext()
        this.recalculateTotals()
        return
      }
      if (!this.form.contact_pincode || this.form.contact_pincode.length !== 6 || !this.form.order_items.length) {
        this.form.shipping_charge = 0
        this.form.cod_fee = 0
        this.clearShippingRateContext()
        this.recalculateTotals()
        return
      }

      axios.post(`${this.$apiAdress}/api/cal_cod_charges`, {
        organizationId: Number(this.$store?.state?.activeOrganisationId || 0),
        pincode: this.form.contact_pincode,
        quantity: this.form.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
        total: Number(this.form.total || 0),
        qtyAYS: 0,
        total_weight_grams: this.orderTotalWeightGrams,
        paymentMethod: this.form.payment_method,
        shippingMethod: this.form.shipping_method
      }, { timeout: 15000 })
        .then((response) => {
          this.form.shipping_charge = Number(response.data?.base_shipping ?? response.data?.cod_charge ?? 0)
          this.form.cod_fee = Number(response.data?.cod_fee || 0)
          this.applyShippingRateContext(response.data)
          this.recalculateTotals()
        })
        .catch((error) => {
          console.log(error)
          this.form.shipping_charge = 0
          this.form.cod_fee = 0
          this.clearShippingRateContext()
          this.recalculateTotals()
        })
    },
    recalculateTotals () {
      const subtotal = this.form.order_items.reduce((sum, item) => {
        item.total = Number(item.quantity || 0) * Number(item.price || 0)
        return sum + Number(item.total || 0)
      }, 0)

      const qty = this.form.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
      // item_count is the total BOTTLE count (sum of line quantities), not the
      // number of product lines — it feeds the "Bottles Sold" tiles, the
      // leaderboard, and the Tally/role-wise exports, all of which mean bottles.
      this.form.item_count = qty
      // orders.weight is PURE GRAMS (India Post dispatch excel prints it as
      // grams — policy is integer grams, e.g. 10 kg → 10000). Use the real
      // catalogue weight sum, NOT the old 0.201 kg/bottle flat which wrote
      // kilograms into a grams column and made the shipping sheet ~1000x light.
      this.form.weight = Math.round(this.orderTotalWeightGrams)
      this.form.total = Number(subtotal.toFixed(2))

      // Preset toggle is ON: sync the discount from the eligible
      // buckets on every recalculation (one per product-scoped line
      // plus at most one for the aggregated org-wide bucket). The
      // applied discount is always a flat AMOUNT — the sum across
      // buckets. Auto-disengage when nothing qualifies any more.
      if (this.applyPreset) {
        const amount = this.eligibleDiscountAmount
        if (amount <= 0) {
          this.applyPreset = false
          this.discountMode = 'amount'
          this.discountInput = 0
        } else {
          this.discountMode = 'amount'
          this.discountInput = amount
        }
      }

      if (this.discountMode === 'percent') {
        this.form.discount = Number(((this.form.total * Number(this.discountInput || 0)) / 100).toFixed(2))
      } else {
        this.form.discount = Number(Number(this.discountInput || 0).toFixed(2))
      }

      this.form.total_after_discount = Number((this.form.total - this.form.discount).toFixed(2))
      this.form.total_shipping_charge = Number((Number(this.form.shipping_charge || 0) + Number(this.form.cod_fee || 0)).toFixed(2))

      const gstPercent = this.includeGstin ? Number(this.form.gst_percentage || 5) : 0
      this.form.gst_percentage = gstPercent
      // Product prices are MRP — already inclusive of GST. Extract the tax
      // for reporting; do NOT add it to grand_total (would double-count).
      this.form.gst_amt = gstPercent > 0
        ? Number(((this.form.total_after_discount * gstPercent) / (100 + gstPercent)).toFixed(2))
        : 0

      const stateCode = String(this.selectedGstState?.state_code || '').toUpperCase()
      this.form.gst_state_code = stateCode
      if (stateCode === 'KL') {
        this.form.cgst_amt = Number((this.form.gst_amt / 2).toFixed(2))
        this.form.sgst_amt = Number((this.form.gst_amt / 2).toFixed(2))
        this.form.igst_amt = 0
      } else {
        this.form.cgst_amt = 0
        this.form.sgst_amt = 0
        this.form.igst_amt = this.form.gst_amt
      }

      this.form.grand_total = Number((this.form.total_after_discount + this.form.total_shipping_charge).toFixed(2))
      this.amountToPay = this.form.grand_total
    },
    openSubmitConfirm () {
      this.errorMessage = ''
      this.successMessage = ''
      if (!this.isFormReady) {
        this.errorMessage = 'Complete all required customer, address, payment, and item details.'
        return
      }
      if (this.form.payment_method === 'adjustment' && !(this.form.adjusted_order || '').trim()) {
        this.errorMessage = 'Enter the referring order number this adjustment is settled against.'
        return
      }
      if (this.shippingZeroReason) {
        this.errorMessage = `Cannot place order — ${this.shippingZeroMessage}`
        this.focusContactPincode()
        return
      }
      this.showSubmitConfirm = true
    },
    focusContactPincode () {
      this.pincodeAttentionPulse = false
      this.$nextTick(() => {
        const input = this.$refs.contactPincodeInput
        const el = input?.$el || input
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        if (input?.focus) input.focus()
        this.pincodeAttentionPulse = true
        setTimeout(() => { this.pincodeAttentionPulse = false }, 2500)
      })
    },
    cancelSubmitConfirm () {
      if (this.isSubmitting) return
      this.showSubmitConfirm = false
    },
    confirmSubmit () {
      // Defence-in-depth: openSubmitConfirm already validates, but if a
      // caller bypasses it (devtools, future programmatic trigger), bail
      // out instead of POSTing an invalid order.
      if (!this.isFormReady) {
        this.errorMessage = 'Complete all required customer, address, payment, and item details.'
        this.showSubmitConfirm = false
        return
      }
      if (this.shippingZeroReason) {
        this.errorMessage = `Cannot place order — ${this.shippingZeroMessage}`
        this.showSubmitConfirm = false
        this.focusContactPincode()
        return
      }

      this.errorMessage = ''
      this.successMessage = ''
      this.isSubmitting = true

      const selectedState = this.selectedGstState
      const isMarketplace = MARKETPLACE_ORDER_TYPES.includes(this.form.order_type)

      const payload = {
        contact_id: this.form.contact_id || undefined,
        contact_name: this.form.contact_name,
        contact_address: this.form.contact_address,
        // Marketplace orders are billed under the fixed placeholder verbatim —
        // never country-code-prefixed — so the saved value is exactly
        // 0000000001 as expected downstream.
        contact_phone: isMarketplace ? MARKETPLACE_PLACEHOLDER_PHONE : this.fullContactPhone,
        contact_phone_2: isMarketplace ? MARKETPLACE_PLACEHOLDER_PHONE : (this.fullAlternatePhone || this.fullContactPhone),
        contact_pincode: this.form.shipping_type === 'domestic' ? this.form.contact_pincode : 0,
        contact_postofficename: this.form.shipping_type === 'domestic' ? this.form.contact_postofficename : 'NA',
        contact_district: this.form.contact_district,
        contact_state: this.form.contact_state,
        contact_gstn: this.includeGstin ? this.form.contact_gstn : null,
        country: this.form.country || 'India',
        city: this.form.city || null,
        shipping_type: this.form.shipping_type,
        local_body_name: this.form.local_body_name || null,
        local_body_type_name: this.form.local_body_type_name || null,
        ward_no: this.form.ward_no || 0,
        billing_name: this.addBillingAddress ? this.form.billing_name : null,
        billing_address: this.addBillingAddress ? this.form.billing_address : null,
        billing_pincode: this.addBillingAddress ? this.form.billing_pincode : null,
        billing_postofficename: this.addBillingAddress ? this.form.billing_postofficename : null,
        billing_district: this.addBillingAddress ? this.form.billing_district : null,
        billing_state: this.addBillingAddress ? this.form.billing_state : null,
        order_type: this.form.order_type,
        // Marketplace identifiers — marketplace_invoice_date is required by
        // the backend for marketplace orders (it becomes order_date); the
        // others map to their like-named columns. Omitted entirely for
        // non-marketplace orders.
        ...(isMarketplace ? {
          marketplace_order_id: this.form.marketplace_order_id || null,
          invoice_number: this.form.invoice_number || null,
          tracking_id: this.form.tracking_id || null,
          marketplace_invoice_date: this.form.marketplace_invoice_date || null
        } : {}),
        item_count: this.form.item_count,
        weight: this.form.weight,
        total: this.form.total,
        discount: this.form.discount,
        total_after_discount: this.form.total_after_discount,
        shipping_charge: Number(this.form.shipping_charge || 0),
        cod_charge: Number(this.form.cod_fee || 0),
        total_shipping_charge: this.form.total_shipping_charge,
        grand_total: this.form.grand_total,
        gst_percentage: this.form.gst_percentage,
        cgst_amt: this.form.cgst_amt,
        sgst_amt: this.form.sgst_amt,
        igst_amt: this.form.igst_amt,
        gst_amt: this.form.gst_amt,
        gst_state_code: selectedState?.state_code || this.form.gst_state_code || '',
        gst_stin: selectedState?.state_tin || null,
        payment_method: this.form.payment_method,
        adjusted_order: this.form.payment_method === 'adjustment' ? (this.form.adjusted_order || '').trim() : null,
        payment_status: this.form.payment_status,
        order_status: this.form.order_status || 'confirmed',
        payment_date: this.form.payment_date || (this.form.payment_method === 'google_pay' ? new Date().toISOString().slice(0, 10) : null),
        shipping_method: this.form.shipping_method,
        shipping_status: this.form.shipping_status,
        order_items: this.form.order_items.map((item) => ({
          // Send the row id back so the backend's update() can match it
          // and UPDATE in place — without this every line item is treated
          // as new and inserted again, duplicating the order's contents.
          id: item.id || null,
          item_id: Number(item.item_id),
          name: item.name,
          sku: item.sku,
          shortcode: item.shortcode,
          quantity: Number(item.quantity),
          price: Number(item.price),
          total: Number(item.total)
        })),
        // Only org 1 surfaces the Customer Crops section; for every
        // other org we omit the key entirely (axios drops `undefined`)
        // so the backend's replace-on-save doesn't fire and wipe the
        // contact's existing crops.
        crops_data: this.showCropsSection
          ? this.allCrops.map((crop) => ({
            crop: crop.crop,
            area: Number(crop.area || 0),
            unit: crop.unit || 'cent',
            count: Number(crop.count || 0)
          }))
          : undefined,
        is_call: false,
        telesales_form: false
      }

      const isEdit = Boolean(this.editOrderId)

      // care_of (telesales credit) is resolved authoritatively on the
      // server at CREATE time — it's tagged to the most recent call placed
      // by a tele caller in the ORDER's product organisation, so credit
      // follows the product line even when a later call came from another
      // org's desk. We no longer send a cross-org guess from here. On edit
      // the server leaves the existing care_of untouched.

      const url = isEdit
        ? `${this.$apiAdress}/api/orders/${this.editOrderId}`
        : `${this.$apiAdress}/api/orders`

      // The `update` validator requires `id` and `order_date` in the body
      // (see orderController::update). Add them only on edit so the create
      // path keeps its existing shape. `deleted_items` is required by the
      // backend's edit loop — must be present even when nothing was
      // removed, otherwise the controller hits an undefined-index error
      // mid-save and 500s AFTER the items have already been written.
      // For marketplace orders order_date IS the invoice date, so an edit
      // must re-send the invoice date rather than stamping today — otherwise
      // the PUT would overwrite the original invoice date with the edit date.
      const editOrderDate = isMarketplace
        ? (this.form.marketplace_invoice_date || new Date().toISOString().slice(0, 10))
        : new Date().toISOString().slice(0, 10)
      const requestPayload = isEdit
        ? {
          ...payload,
          id: this.editOrderId,
          order_date: editOrderDate,
          deleted_items: this.deletedItemIds
        }
        : payload

      const httpCall = isEdit
        ? axios.put(url, requestPayload, { timeout: 20000 })
        : axios.post(url, requestPayload, { timeout: 20000 })

      httpCall
        .then((response) => {
          if (response?.data?.error) {
            const err = typeof response.data.error === 'string' ? response.data.error : JSON.stringify(response.data.error)
            this.errorMessage = err
            return
          }
          if (isEdit) {
            // Edit flow stays inline — the order already exists in the
            // Orders list where the user can re-print / re-send. Close
            // the submit-confirm dialog so the success banner is visible
            // and the user isn't left staring at a stale modal.
            this.showSubmitConfirm = false
            this.successMessage = response?.data?.message || 'Order updated successfully.'
          } else {
            // Snapshot the form BEFORE resetForm so the success modal
            // can still show contact name + phone after the form clears.
            this.lastSaved = this.buildSaleSnapshot(response?.data?.OrderId)
            this.lastSavedRequiresApproval = Boolean(response?.data?.requires_approval)
            this.showSubmitConfirm = false
            this.showSuccessModal = true
            this.resetForm(false)
          }
        })
        .catch((error) => {
          console.log(error)
          const msg = error?.response?.data?.message || error?.message || (isEdit ? 'Unable to update order.' : 'Unable to save order.')
          this.errorMessage = msg
        })
        .finally(() => {
          this.isSubmitting = false
        })
    },
    buildSaleSnapshot (orderId) {
      // Plain-object snapshot of the just-saved order so the success
      // modal survives the resetForm() that follows. Mirrors the field
      // set TeleSales captures for the same modal.
      return {
        orderId: orderId || null,
        contactName: this.form.contact_name,
        contactPhone: this.form.contact_phone,
        dialCode: this.dialCode,
        itemCount: this.form.order_items.length,
        grandTotal: this.form.grand_total,
        items: this.form.order_items.map((item) => ({
          name: item.name,
          quantity: Number(item.quantity || 0),
          total: Number(item.total || 0)
        })),
        discount: Number(this.form.discount || 0),
        shipping: Number(this.form.shipping_charge || 0),
        codFee: Number(this.form.cod_fee || 0),
        gst: Number(this.form.gst_amt || 0),
        paymentMethod: this.form.payment_method
      }
    },
    closeSuccessModal () {
      this.showSuccessModal = false
      this.lastSaved = null
      this.lastSavedRequiresApproval = false
    },
    invoiceUrl () {
      if (!this.lastSaved?.orderId) return ''
      return `${this.$apiAdress}/invoices?orders=${this.lastSaved.orderId}`
    },
    downloadInvoice () {
      const url = this.invoiceUrl()
      if (!url) return
      window.open(`${url}&autoprint=1`, '_blank', 'noopener')
    },
    buildSaleWhatsappMessage () {
      const snap = this.lastSaved
      if (!snap) return ''
      const customer = snap.contactName || 'Customer'
      const lines = [`Hello ${customer},`, '', 'Thank you for your order. Here are the details:']
      snap.items.forEach((item) => {
        lines.push(`${item.quantity} × ${item.name} — ${this.formatCurrency(item.total)}`)
      })
      if (snap.discount > 0) lines.push(`Discount: −${this.formatCurrency(snap.discount)}`)
      if (snap.shipping > 0) lines.push(`Shipping: ${this.formatCurrency(snap.shipping)}`)
      if (snap.codFee > 0) lines.push(`COD Fee: ${this.formatCurrency(snap.codFee)}`)
      if (snap.gst > 0) lines.push(`GST: ${this.formatCurrency(snap.gst)}`)
      lines.push(`*Total: ${this.formatCurrency(snap.grandTotal)}*`)
      if (snap.paymentMethod) lines.push(`Payment: ${this.formatLabel(snap.paymentMethod)}`)
      const invoice = this.invoiceUrl()
      if (invoice) {
        lines.push('')
        lines.push(`Invoice: ${invoice}`)
      }
      return lines.join('\n')
    },
    sendInvoiceWhatsapp () {
      const snap = this.lastSaved
      if (!snap?.contactPhone) return
      const phone = String(snap.contactPhone).replace(/\D/g, '')
      const dial = String(snap.dialCode || '91').replace(/\D/g, '')
      const message = this.buildSaleWhatsappMessage()
      const waUrl = `https://wa.me/${dial}${phone}?text=${encodeURIComponent(message)}`
      window.open(waUrl, '_blank', 'noopener')
    },
    resetForm (keepBootstrap = true) {
      const phone = keepBootstrap ? this.form.contact_phone : ''
      // Alt phone is its own field now — preserve it on
      // keepBootstrap resets but clear on a full reset.
      const altPhone = keepBootstrap ? this.form.contact_phone_2 : ''
      this.form = {
        contact_id: null,
        contact_name: '',
        contact_phone: phone,
        contact_phone_2: altPhone,
        contact_address: '',
        contact_pincode: '',
        contact_postofficename: '',
        contact_district: '',
        contact_state: '',
        contact_gstn: '',
        country: 'India',
        city: '',
        shipping_type: 'domestic',
        local_body_name: '',
        local_body_type_name: '',
        ward_no: 0,
        billing_name: '',
        billing_address: '',
        billing_pincode: '',
        billing_postofficename: '',
        billing_district: '',
        billing_state: '',
        order_type: ORDER_TYPE_DEFAULT,
        // Amazon-only identifiers. marketplace_order_id / invoice_number /
        // tracking_id map straight to the like-named order columns;
        // marketplace_invoice_date drives order_date for amazon orders
        // (orderController::store).
        marketplace_order_id: '',
        invoice_number: '',
        tracking_id: '',
        marketplace_invoice_date: '',
        item_count: 0,
        weight: 0,
        total: 0,
        discount: 0,
        total_after_discount: 0,
        shipping_charge: 0,
        cod_fee: 0,
        total_shipping_charge: 0,
        grand_total: 0,
        gst_percentage: this.includeGstin ? 5 : 0,
        cgst_amt: 0,
        sgst_amt: 0,
        igst_amt: 0,
        gst_amt: 0,
        gst_state_code: '',
        payment_method: '',
        adjusted_order: '',
        payment_date: '',
        payment_status: 'payment_pending',
        order_status: 'confirmed',
        shipping_method: '',
        shipping_status: 'for_print',
        order_items: []
      }

      this.amountToPay = 0
      this.clearShippingRateContext()
      this.deletedItemIds = []
      this.productDraft = { item_id: '', quantity: 1 }
      this.discountMode = 'amount'
      this.discountInput = 0
      this.applyPreset = false
      this.selectedLocalBodyName = ''
      this.postOfficeRecords = []
      this.postOfficeOptions = []
      this.localBodyOptions = []
      this.contactCrops = []
      this.existingCrops = []
      this.contactCalls = []
      this.contactOrders = []
      this.contactGstinOnFile = ''
      this.codBlocked = false
      this.historyCollapsed = false
      this.addBillingAddress = false
      this.marketplaceMode = ''
      this.searchHint = ''
      this.recalculateTotals()
    }
  }
}
</script>

<style scoped>
.oform-page {
  padding: 0.25rem 0;
}

.oform-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.oform-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.oform-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.oform-toolbar__accent {
  width: 4px;
  height: 22px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.oform-toolbar__meta {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  font-size: 0.82rem;
}

.oform-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.oform-grid {
  row-gap: 1rem;
}

.oform-main {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.oform-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.oform-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.oform-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  letter-spacing: -0.01em;
}

.oform-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.oform-section__body {
  padding: 1rem;
}

.oform-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.oform-label__required {
  margin-left: 0.2rem;
  color: #dc2626;
  font-weight: 700;
}

.oform-label__status {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: none;
  letter-spacing: 0;
}

.oform-label__status--warn {
  color: #b45309;
}

.oform-label-inline {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0 0 0.15rem;
}

.oform-cod-block-note {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.4rem;
  padding: 0.45rem 0.6rem;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 8px;
  font-size: 0.74rem;
  font-weight: 500;
  line-height: 1.3;
}

.oform-cod-block-note svg {
  flex: 0 0 auto;
  color: #b91c1c;
}

.oform-adjusted-against {
  margin-top: 0.5rem;
}

.oform-shipping-zero {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.4rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid #fcd34d;
  background: #fffbeb;
  color: #78350f;
  border-radius: 8px;
  font-size: 0.74rem;
  line-height: 1.35;
}

.oform-shipping-zero > svg {
  flex: 0 0 auto;
  color: #b45309;
  margin-top: 0.15rem;
}

.oform-shipping-zero__body {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1 1 auto;
  min-width: 0;
}

.oform-shipping-zero__body strong {
  font-weight: 700;
  color: #92400e;
}

.oform-shipping-zero__btn {
  flex: 0 0 auto;
  align-self: center;
  padding: 0.32rem 0.7rem;
  border-radius: 6px;
  border: 1px solid #f59e0b;
  background: #ffffff;
  color: #92400e;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.oform-shipping-zero__btn:hover {
  background: #f59e0b;
  border-color: #d97706;
  color: #ffffff;
}

.oform-shipping-zero__btn:focus { outline: none; }
.oform-shipping-zero__btn::-moz-focus-inner { border: 0; }
.oform-shipping-zero__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.32);
}

.is-attention-pulse >>> .app-input__field {
  animation: oformPincodeAttention 0.6s ease-in-out 0s 3;
  border-color: #f59e0b;
}

@keyframes oformPincodeAttention {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
    border-color: #f59e0b;
  }
  50% {
    box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.35);
    border-color: #d97706;
  }
}

.head-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  cursor: pointer;
  user-select: none;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.head-toggle input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #16a07a;
  cursor: pointer;
  margin: 0;
}

.marketplace-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0;
  padding: 0.34rem 0.72rem;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  background: #ffffff;
  cursor: pointer;
  user-select: none;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.marketplace-toggle:hover {
  border-color: #cfd6e4;
}

.marketplace-toggle.is-active {
  border-color: #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
  box-shadow: 0 1px 4px rgba(22, 160, 122, 0.18);
}

.marketplace-toggle input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #16a07a;
  cursor: pointer;
  margin: 0;
}

.marketplace-toggle:focus-within {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.marketplace-phone-note {
  margin-top: 0.35rem;
  padding: 0.55rem 0.7rem;
  font-size: 0.78rem;
  color: #6b7280;
  background: #fbfcfe;
  border: 1px dashed #e5e9f2;
  border-radius: 8px;
}

.marketplace-phone-note strong {
  color: #0e5b46;
  letter-spacing: 0.02em;
}

.oform-page .form-group {
  margin-bottom: 0.85rem;
}

.oform-page >>> .btn {
  border-radius: 8px;
  font-weight: 600;
}

.oform-page >>> .form-control {
  font-size: 0.85rem;
  border-radius: 8px;
  min-height: 36px;
  height: 36px;
  border-color: #d8dde6;
}

.oform-page >>> .form-control:focus {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.phone-row {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.phone-row__country {
  flex: 0 0 130px;
}

.phone-row__input {
  flex: 1 1 200px;
}

.phone-row__btn {
  min-width: 120px;
  height: 36px;
}

.phone-row__hint {
  display: block;
  margin-top: 0.4rem;
  min-height: 1rem;
  font-size: 0.78rem;
}

.product-add {
  display: grid;
  grid-template-columns: 1fr 110px 1fr auto;
  gap: 0.6rem;
  align-items: end;
  padding: 0.85rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  margin-top: 0.25rem;
}

.product-add__field {
  display: flex;
  flex-direction: column;
}

.product-add__field--qty {
  max-width: 110px;
}

.product-add__action {
  min-width: 100px;
}

.product-add__action .btn {
  height: 36px;
}

.item-list {
  list-style: none;
  margin: 0.85rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.item-list__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 110px 110px auto;
  gap: 0.85rem;
  align-items: center;
  padding: 0.65rem 0.85rem;
  border: 1px solid #e6eaf3;
  border-radius: 10px;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.item-list__row:hover {
  border-color: #c7d2e0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}

.item-list__main {
  min-width: 0;
}

.item-list__name {
  font-weight: 600;
  color: #1f2a44;
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-list__meta {
  font-size: 0.76rem;
  color: #6b7280;
  margin-top: 0.1rem;
}

.item-list__qty {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.item-list__total {
  display: flex;
  flex-direction: column;
}

.item-list__total strong {
  color: #1f2a44;
  font-size: 0.95rem;
}

.item-list__remove {
  white-space: nowrap;
}

.oform-empty {
  text-align: center;
  padding: 1.25rem;
  color: #6b7280;
  font-size: 0.85rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
}

.discount-row {
  display: flex;
  gap: 0.5rem;
}

.discount-row.is-locked { opacity: 0.85; }

.discount-row__mode {
  max-width: 80px;
}

/* Eligible-discount banner shown above the discount row when the
   order qty matches a configured preset tier. The toggle is the
   only path non-privileged roles can use to apply a discount. */
.preset-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  padding: 0.7rem 0.95rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f5fbf8 100%);
  border: 1px solid #c7ebda;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.06);
  flex-wrap: wrap;
}

.preset-banner__info {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  min-width: 0;
}

.preset-banner__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #16a07a;
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.18);
  flex-shrink: 0;
}

.preset-banner__title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.01em;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.preset-banner__info-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid #c7ebda;
  background: #ffffff;
  color: #0e5b46;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.preset-banner__info-btn:hover {
  border-color: #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.12);
}

.preset-banner__info-btn:focus { outline: none; }
.preset-banner__info-btn::-moz-focus-inner { border: 0; }
.preset-banner__info-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.preset-banner__sub {
  font-size: 0.75rem;
  color: #4b5563;
  margin-top: 0.1rem;
}

.preset-banner__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0;
  cursor: pointer;
  user-select: none;
}

.preset-banner__toggle input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.preset-banner__track {
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 999px;
  background: #d1d5db;
  transition: background 0.18s ease;
  flex-shrink: 0;
}

.preset-banner__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
  transition: left 0.18s ease;
}

.preset-banner__toggle.is-on .preset-banner__track {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.preset-banner__toggle.is-on .preset-banner__thumb {
  left: 18px;
}

.preset-banner__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #0e5b46;
  min-width: 50px;
}

.preset-pill {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.06rem 0.4rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  background: #dff5ea;
  color: #0e5b46;
  letter-spacing: 0.02em;
}

.preset-pill--muted {
  background: #f1f5f9;
  color: #475569;
}

.summary-line__label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.summary-distance-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.08rem 0.45rem;
  border-radius: 999px;
  border: 1px solid #c7ebda;
  background: #ecfdf5;
  color: #0e5b46;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.summary-distance-chip:hover {
  background: #dff5ea;
  border-color: #16a07a;
  color: #0e5b46;
}

.summary-distance-chip:focus { outline: none; }
.summary-distance-chip:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.25);
}

.summary-distance-chip svg { flex: 0 0 auto; opacity: 0.85; }

.amount-due {
  margin-top: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
}

.amount-due__body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.amount-due__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7280;
}

.amount-due__value {
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0e5b46;
  line-height: 1.15;
}

.amount-due__hint {
  font-size: 0.72rem;
  font-weight: 500;
  color: #6b7280;
  text-align: right;
}

.oform-aside {
  display: flex;
  flex-direction: column;
}

.oform-sticky {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: sticky;
  top: 1rem;
}

.crop-table {
  border: 1px solid #e6eaf3;
  border-radius: 10px;
  overflow: hidden;
}

.crop-table >>> .table {
  margin-bottom: 0;
  font-size: 0.82rem;
}

.crop-table >>> thead th {
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
  border-top: 0;
  font-weight: 700;
  color: #4f5560;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.crop-table >>> tbody td {
  color: #1f2a44;
  border-color: #eef1f7;
}

.crop-add {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr auto;
  gap: 0.6rem;
  align-items: end;
  padding: 0.85rem;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  margin-top: 0.85rem;
}

.crop-add__field {
  display: flex;
  flex-direction: column;
}

.crop-add__action {
  min-width: 90px;
}

.crop-add__action .btn {
  height: 36px;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.45rem 0;
  border-bottom: 1px dashed #eef1f7;
  font-size: 0.85rem;
  color: #4b5563;
}

.summary-line strong {
  color: #1f2a44;
}

.summary-line--total {
  margin-top: 0.4rem;
  padding-top: 0.6rem;
  border-top: 2px solid #1f2a44;
  border-bottom: 0;
  font-size: 0.95rem;
}

.summary-line--total strong {
  font-size: 1.1rem;
}

.summary-line--note {
  margin-top: 0.15rem;
  padding: 0.25rem 0 0;
  border-bottom: 0;
  color: #6b7280;
  font-size: 0.74rem;
  font-style: italic;
}

.summary-line--note strong {
  color: #6b7280;
  font-weight: 600;
  font-style: normal;
}

.summary-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.85rem;
}

.summary-actions__primary {
  height: 44px;
  font-size: 0.95rem;
}

.summary-actions__whatsapp {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  height: 40px;
  padding: 0 0.95rem;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(135deg, #15a25b 0%, #25d366 100%);
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.28);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  font-family: inherit;
}

.summary-actions__whatsapp:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(37, 211, 102, 0.38);
  filter: brightness(1.05);
}

.summary-actions__whatsapp:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.summary-actions__whatsapp:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.summary-actions__reset {
  margin-top: 0.25rem;
}

/* Contact history */
.history-section .oform-section__head h2 {
  color: #0e5b46;
}

.history-section__head {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.history-section__head:hover {
  background: #f4f8f6;
}

.history-section__head:focus {
  outline: none;
  background: #f0fbf6;
}

.history-section__head-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
}

.history-section__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.75rem;
  border: 1px solid #d6ebe1;
  transition: transform 0.2s ease;
}

.history-section__chevron.is-open {
  transform: rotate(180deg);
}

.history-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.85rem;
}

.history-stat {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #f0fbf6 0%, #e7f5ee 100%);
  border: 1px solid #d6ebe1;
}

.history-stat__label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4f7868;
}

.history-stat__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0e5b46;
}

.history-stat__value--small {
  font-size: 0.82rem;
  line-height: 1.2;
}

.history-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}

.history-col {
  min-width: 0;
}

.history-title {
  margin: 0 0 0.5rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 280px;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.history-item {
  padding: 0.55rem 0.7rem;
  border: 1px solid #e6eaf3;
  border-radius: 8px;
  background: #ffffff;
}

.history-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.history-item__date {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
}

.history-item__strong {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2a44;
}

.history-item__amount {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0e5b46;
  white-space: nowrap;
}

.history-item__sub {
  margin-top: 0.2rem;
  font-size: 0.76rem;
  color: #6b7280;
}

.history-dot {
  margin: 0 0.25rem;
  color: #cbd5e1;
}

.history-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
}

.history-pill.is-success { background: #dff5ea; color: #0e5b46; }
.history-pill.is-info    { background: #e0edff; color: #1e40af; }
.history-pill.is-warn    { background: #fef3c7; color: #92400e; }
.history-pill.is-muted   { background: #f1f5f9; color: #64748b; }
.history-pill.is-default { background: #eef1f7; color: #4b5563; }

.history-products {
  list-style: none;
  margin: 0.4rem 0 0;
  padding: 0.4rem 0 0;
  border-top: 1px dashed #eef1f7;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.history-products li {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.78rem;
  color: #4b5563;
}

.history-products__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-products__qty {
  color: #6b7280;
  font-weight: 600;
  white-space: nowrap;
}

.history-empty {
  padding: 0.85rem;
  text-align: center;
  font-size: 0.8rem;
  color: #6b7280;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
}

@media (max-width: 991px) {
  .oform-sticky {
    position: static;
  }

  .history-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .history-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .product-add {
    grid-template-columns: 1fr 1fr;
  }

  .product-add__field--qty {
    max-width: none;
  }

  .product-add__action {
    grid-column: 1 / -1;
  }

  .item-list__row {
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem 0.85rem;
  }

  .item-list__main {
    grid-column: 1 / -1;
  }

  .item-list__remove {
    grid-column: 1 / -1;
  }

  .phone-row__country {
    flex: 1 1 120px;
  }

  .phone-row__btn {
    flex: 1 1 100%;
  }

  .amount-due {
    flex-wrap: wrap;
  }

  .amount-due__hint {
    width: 100%;
    text-align: left;
  }

  .crop-add {
    grid-template-columns: 1fr 1fr;
  }

  .crop-add__action {
    grid-column: 1 / -1;
  }
}

/* Sale success modal — kept in sync with TeleSales.vue. The
   `Order Placed!` celebration after a successful create, with
   download-invoice and WhatsApp actions. */
.sale-success {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.sale-success__panel {
  position: relative;
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.32), 0 4px 12px rgba(15, 23, 42, 0.08);
  padding: 1.85rem 1.5rem 1.4rem;
  text-align: center;
  animation: sale-success-pop 0.32s cubic-bezier(0.18, 1.2, 0.4, 1) both;
}

.sale-success__close {
  position: absolute;
  top: 0.6rem;
  right: 0.6rem;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.sale-success__close:hover {
  background: #f3f4f6;
  color: #1f2a44;
}

.sale-success__animation {
  position: relative;
  width: 96px;
  height: 96px;
  margin: 0 auto 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sale-success__ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(22, 160, 122, 0.22);
  transform: scale(0);
  animation: sale-success-ring 1.4s ease-out 0.05s infinite;
}

.sale-success__ring--delay {
  animation-delay: 0.7s;
}

.sale-success__check {
  position: relative;
  width: 96px;
  height: 96px;
  filter: drop-shadow(0 4px 10px rgba(22, 160, 122, 0.28));
}

.sale-success__check-circle {
  fill: none;
  stroke: #16a07a;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 176;
  stroke-dashoffset: 176;
  transform-origin: center;
  transform: rotate(-90deg);
  animation: sale-success-draw 0.55s ease-out 0.1s forwards;
}

.sale-success__check-tick {
  fill: none;
  stroke: #16a07a;
  stroke-width: 4.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: sale-success-draw-tick 0.32s ease-out 0.55s forwards;
}

.sale-success__title {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0e5b46;
  letter-spacing: -0.01em;
}

.sale-success__subtitle {
  margin: 0 0 1rem;
  font-size: 0.85rem;
  color: #4b5563;
}

.sale-success__notice {
  margin: 0 0 1rem;
  padding: 0.55rem 0.85rem;
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 8px;
  font-size: 0.78rem;
  line-height: 1.4;
  text-align: left;
}

.sale-success__meta {
  margin: 0 auto 1.25rem;
  padding: 0.6rem 0.85rem;
  background: #f8fafc;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  display: grid;
  gap: 0.35rem;
  text-align: left;
  font-size: 0.82rem;
}

.sale-success__meta-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.sale-success__meta-row dt {
  margin: 0;
  color: #6b7280;
  font-weight: 500;
}

.sale-success__meta-row dd {
  margin: 0;
  color: #1f2a44;
  font-weight: 600;
}

.sale-success__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}

.sale-success__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 40px;
  padding: 0 0.9rem;
  border-radius: 9px;
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: inherit;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.sale-success__btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.sale-success__btn--ghost {
  background: #ffffff;
  border-color: #d8dde6;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.sale-success__btn--ghost:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f0fbf6;
}

.sale-success__btn--primary {
  background: linear-gradient(135deg, #15a25b 0%, #25d366 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.32);
}

.sale-success__btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(37, 211, 102, 0.4);
  filter: brightness(1.05);
}

.sale-success__btn--primary:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.sale-success-fade-enter-active,
.sale-success-fade-leave-active {
  transition: opacity 0.18s ease;
}

.sale-success-fade-enter,
.sale-success-fade-leave-to {
  opacity: 0;
}

@keyframes sale-success-pop {
  0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes sale-success-draw {
  to { stroke-dashoffset: 0; }
}

@keyframes sale-success-draw-tick {
  to { stroke-dashoffset: 0; }
}

@keyframes sale-success-ring {
  0%   { transform: scale(0.4); opacity: 0.55; }
  80%  { opacity: 0; }
  100% { transform: scale(1.35); opacity: 0; }
}
</style>
